import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import DocumentViewer from '../../components/DocumentViewer';
import { AGENT_NAME_SHORT } from '../../brand';
import ArcOrb from '../../components/ArcOrb';
import ItemTypeBadge from '../../components/ItemTypeBadge';
import ChecklistFilterPopover from '../../components/checklist/ChecklistFilterPopover';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import useChatEvents from '../../hooks/useChatEvents';
import useHelpTarget from '../../hooks/useHelpTarget';
import HelpLink from '../../components/help/HelpLink';

const STATUSES = [
  { key: 'not_reviewed', label: 'Needs Review',   icon: '👀', cls: 'cl-not-reviewed' },
  { key: 'matched',      label: 'Confirmed Match', icon: '✅', cls: 'cl-matched' },
  { key: 'no_match',     label: 'No Match',       icon: '❌', cls: 'cl-no-match' },
  { key: 'n_a',          label: 'N/A',            icon: '➖', cls: 'cl-na' },
];

// Confidence color buckets for the per-candidate detail rendering inside
// UnifiedItemRow. These were referenced but never defined — historically
// hidden behind a hasCandidates branch that was always false. The new
// pipeline populates candidates, so the missing definitions started
// throwing 'Can't find variable: confidenceLevel'. Define centrally.
function confidenceLevel(score) {
  const s = typeof score === 'number' ? score : 0;
  if (s >= 0.8) return 'high';
  if (s >= 0.55) return 'medium';
  return 'low';
}
// Paralegal-readable label for a raw composite score. "Strong" maps to
// the EXACT + NEAR tiers (>= 0.80), "Possible" to PARTIAL (>= 0.40), and
// "Weak" to below-partial. Used wherever we'd otherwise show "62%" —
// percentages without a calibrated meaning aren't useful to a lawyer.
function confidenceLabel(score) {
  const s = typeof score === 'number' ? score : 0;
  if (s >= 0.80) return 'Strong';
  if (s >= 0.40) return 'Possible';
  return 'Weak';
}
const CONFIDENCE_COLORS = {
  high:   { border: '#10b981', bg: '#ecfdf5', text: '#065f46' },
  medium: { border: '#f59e0b', bg: '#fffbeb', text: '#92400e' },
  low:    { border: '#9ca3af', bg: '#f9fafb', text: '#4b5563' },
};

const DECISION_STATES = {
  suggested: { label: 'Suggested Match', icon: '🤖' },
  confirmed: { label: 'Confirmed Match', icon: '✅' },
  no_suggestion: { label: 'Needs Review', icon: '👀' },
  no_match: { label: 'No Match', icon: '❌' },
  n_a: { label: 'N/A', icon: '➖' },
};

const REVIEW_REASON_OPTIONS = [
  { value: '', label: 'Optional reason code' },
  { value: 'correct_match', label: 'Correct exact match' },
  { value: 'acceptable_substitute', label: 'Acceptable substitute' },
  { value: 'wrong_document', label: 'Wrong document' },
  { value: 'wrong_checklist_interpretation', label: 'Wrong checklist interpretation' },
  { value: 'too_broad', label: 'Too broad / includes extras' },
  { value: 'insufficient_evidence', label: 'Insufficient evidence' },
  { value: 'missing_candidate', label: 'Missing better candidate' },
];

const DEFAULT_REVIEW_FEEDBACK = {
  reasonCode: '',
  reviewerNote: '',
};

const PER_PAGE = 50;

function pairKey(itemId, docId) { return `${itemId}::${docId}`; }

function getDecisionState(item, latestRunBucket = null) {
  if (!item) return 'no_suggestion';
  if (item.status === 'n_a') return 'n_a';
  if (item.status === 'no_match') return 'no_match';
  if (item.status === 'matched' && item.manually_verified) return 'confirmed';

  const hasSuggestedDoc = !!(
    item.source_document_id
    || item.file_path
    || latestRunBucket?.key === 'review'
  );

  if (hasSuggestedDoc) return 'suggested';
  return 'no_suggestion';
}

export default function ChecklistTab({ caseId }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  // Pool Governance P2: seed the active-checklist resume probes from the
  // case-page dashboard envelope to avoid two extra fetches on mount.
  const { caseDashboard, documents: caseDocuments } = useApp();

  // Overview
  // Help anchor — right-click the reason-code dropdown row or hit ⌘/
  // while it's focused. Opens the reason-codes article scoped. No
  // visible UI added. Callback ref pattern handles the modal's
  // conditional mount lifecycle.
  const reasonCodeHelpRef = useHelpTarget({
    slug: 'discovery-check-reason-codes',
    label: 'Reason code',
    question: 'What do these reason codes mean and which should I pick?',
  });

  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [checklists, setChecklists] = useState([]);
  const [selectedChecklistId, setSelectedChecklistId] = useState('');
  const [checklistsLoading, setChecklistsLoading] = useState(true);
  // Soft-archive support: backend hides archived rows by default; toggle
  // re-fetches with include_archived=true so power-users can see them
  // when intentionally restoring or reviewing audit history.
  const [showArchived, setShowArchived] = useState(false);
  const [archivedCount, setArchivedCount] = useState(0);
  const [activatingChecklist, setActivatingChecklist] = useState(false);
  const [deletingChecklist, setDeletingChecklist] = useState(false);

  // Item browser
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filterSearch, setFilterSearch] = useState('');
  // Multi-select item-type filter. Empty Set = "all types".
  const [filterItemTypes, setFilterItemTypes] = useState(() => new Set());
  // Score-range filter. [0, 100] = no narrowing. The slider operates in
  // 0–100 integer space for cleaner step behavior; we divide by 100
  // before sending to the server.
  //
  // scoreRange is the *committed* value — drives the items + counts
  // fetches via useEffect. scoreDragRange is the *live* value during
  // a drag — drives the slider handle position and the inline label.
  // We commit on drag release (onChangeComplete) so we don't fire a
  // burst of racing requests for every pixel of motion (which made
  // pill counts settle on whichever in-flight response landed last,
  // not necessarily the user's final position).
  const [scoreRange, setScoreRange] = useState(() => [0, 100]);
  const [scoreDragRange, setScoreDragRange] = useState(() => [0, 100]);
  // Filter-pill cross-tab counts. Refetched whenever the active filters
  // change so the user can see "if I toggle X, this many items will match."
  const [filterCounts, setFilterCounts] = useState(null);
  // Bumped after every overview fetch so /checklist/items/counts re-runs in
  // lockstep with /checklist/overview. Mutations (Approve, Mark no match,
  // etc.) call fetchOverview() directly, but fetchFilterCounts only
  // re-fires on filter-state changes — without this tick the banner would
  // go stale (Phase 0 wired the banner to filterCounts).
  const [statsRefreshTick, setStatsRefreshTick] = useState(0);
  const [browserLoading, setBrowserLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());

  // Bulk status — default 'matched' since the dominant flow is now
  // "filter to a bucket of suggestions → select all → confirm".
  const [bulkStatus, setBulkStatus] = useState('matched');
  const [bulkApplying, setBulkApplying] = useState(false);

  // View mode: items vs orphan documents (docs with no checklist match)
  const [viewMode, setViewMode] = useState('items'); // 'items' | 'orphans'
  const [orphanDocs, setOrphanDocs] = useState(null); // {total, documents[]} | null
  const [orphanLoading, setOrphanLoading] = useState(false);

  // Candidate bulk action status
  const [candidateBulkStatus, setCandidateBulkStatus] = useState('matched');

  // Match candidates
  const [activeStrategy, setActiveStrategy] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchError, setMatchError] = useState(null);
  const [matchResult, setMatchResult] = useState(null);
  const [selectedPairs, setSelectedPairs] = useState(new Set());
  const [dismissed, setDismissed] = useState(new Set());
  const [accepting, setAccepting] = useState(false);
  const [acceptResult, setAcceptResult] = useState(null);
  const [fuzzyThreshold, setFuzzyThreshold] = useState(0.3);

  // Agent auto-match
  const [agentActive, setAgentActive] = useState(false);
  const [agentProgress, setAgentProgress] = useState(null);
  const [agentActivity, setAgentActivity] = useState([]);
  const [graphPaused, setGraphPaused] = useState(false);
  const feedRef = useRef(null);

  // Checklist profile run
  const [profileActive, setProfileActive] = useState(false);
  const [profileProgress, setProfileProgress] = useState(null);
  const [profileResult, setProfileResult] = useState(null);

  // Per-checklist match settings
  const [matchSettings, setMatchSettings] = useState({ exclusive_matching: true });
  const [matchSettingsOpen, setMatchSettingsOpen] = useState(false);
  const [matchSettingsSaving, setMatchSettingsSaving] = useState(false);
  const matchSettingsRef = useRef(null);

  // Reflex match-health probe (Phase 2 of the agentic-enrichment plan).
  // Polled after every match-run completion + on mount. When `is_weak`
  // is true the banner surfaces with an "Improve matches" CTA; the
  // banner never auto-fires the action.
  const [matchHealth, setMatchHealth] = useState(null);
  const [improveLoading, setImproveLoading] = useState(false);
  const [improveDismissed, setImproveDismissed] = useState(false);
  // Banner is transient — only shown immediately after the user runs
  // Improve matches. The "is_weak" health probe gates the inline
  // entry-point chip, not the loud banner. Resets on checklist change
  // and on dismiss.
  const [recentlyImproved, setRecentlyImproved] = useState(false);

  // "Find matches" — single-button flow. When the user clicks while
  // items are still un-profiled, we kick off profile first and set
  // this ref so the profile_done event handler auto-fires the match
  // run instead of leaving the user to click again. Cleared on
  // completion or stop.
  const pendingMatchAfterProfileRef = useRef(false);

  // Per-item review (Phase B)
  const [perItemDecisions, setPerItemDecisions] = useState({});  // item_id → 'approve'|'reject'|'n_a'
  const [perItemNotes, setPerItemNotes] = useState({});          // item_id → {reason_code, note}
  const [sessionNarrative, setSessionNarrative] = useState('');

  // Phase D: iteration tracking
  const [matchIteration, setMatchIteration] = useState(0);
  const [iterationCapReached, setIterationCapReached] = useState(false);
  const [costCheckpoint, setCostCheckpoint] = useState(null);

  // Agent ask_user — agent-initiated mid-run question
  const [agentQuestion, setAgentQuestion] = useState(null);  // {question, context, askedAt} | null
  const [agentAnswerDraft, setAgentAnswerDraft] = useState('');

  // Multi-select decision-state filter. Empty Set = "all".
  // Server-side filterable (confirmed / no_suggestion / n_a / no_match /
  // needs_profile_review) and client-side run-band filterable (suggested /
  // ambiguous, derived from matchResult samples) values can coexist.
  // Within the set the semantics is OR; combined with item_type it's AND.
  const [activeBuckets, setActiveBuckets] = useState(() => new Set());

  // Convenience boolean for the bucket pill click handler.
  const toggleBucket = useCallback((key) => {
    setActiveBuckets(prev => {
      const next = new Set(prev);
      if (key === 'all') return new Set();
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);
  const toggleItemType = useCallback((key) => {
    setFilterItemTypes(prev => {
      const next = new Set(prev);
      if (key === 'all' || key === '') return new Set();
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  // Map active-bucket pill keys to the right server filter axis.
  // Decision-state pills (confirmed / no_suggestion / n_a / no_match /
  // profile_review) drive `decision_states`. Band pills (suggested /
  // ambiguous) drive `bands` — server-side via match_candidates so
  // pagination reconciles with pill counts. Replaces the prior
  // client-side runBandFilter that only saw items the SSE samples had
  // populated and broke pagination on big checklists.
  // Filter bucket → server decision_state mapping. "Suggested" was
  // formerly wired to the band axis (band='suggested' = top candidate
  // hit the high-confidence score tier), but the user-facing label
  // matches the row pill "Suggested Match" which reflects DECISION
  // STATE (matcher proposed a candidate, undecided) — not band.
  // Re-mapped 2026-06-01: 'suggested' now goes to decision_states.
  const SERVER_DECISION_STATE_BY_BUCKET = {
    suggested: 'suggested',
    confirmed: 'confirmed',
    needs_review: 'no_suggestion',
    n_a: 'n_a',
    no_match: 'no_match',
    profile_review: 'needs_profile_review',
  };
  const decisionStatesList = useMemo(() => {
    const out = [];
    for (const b of activeBuckets) {
      const s = SERVER_DECISION_STATE_BY_BUCKET[b];
      if (s) out.push(s);
    }
    return out;
  }, [activeBuckets]);

  // Band axis is no longer surfaced in the filter UI (the prior
  // 'suggested' bucket has moved to the decision_state axis).
  const bandsList = useMemo(() => [], []);

  // Reset shim for any legacy callers that expect a setter.
  const setDecisionFilter = useCallback((_v) => setActiveBuckets(new Set()), []);
  const [resolvedReviewItemIds, setResolvedReviewItemIds] = useState(new Set());
  const lastMatchCacheKey = useMemo(
    () => `checklist:last-run:${caseId}:${selectedChecklistId || 'active'}`,
    [caseId, selectedChecklistId],
  );

  // Document detail modal
  const [viewerDocId, setViewerDocId] = useState(null);

  // Feedback loop
  const [lastConversationId, setLastConversationId] = useState(null);
  // Reset epoch — incremented on every Reset. handleMatchEvents reads
  // this via ref and ignores any match_ready/etc. that arrive after a
  // reset bumped the counter, defending against in-flight SSE callbacks
  // resurrecting stale state.
  const resetEpochRef = useRef(0);
  const [reviewFeedback, setReviewFeedback] = useState(DEFAULT_REVIEW_FEEDBACK);
  const [pendingReviewAction, setPendingReviewAction] = useState(null);

  // Reset
  const [resetConfirm, setResetConfirm] = useState(false);


  // Auto-scroll agent feed
  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [agentActivity]);

  // ── Group candidates by item_id ──

  const visibleCandidates = useMemo(() => {
    if (!matchResult?.candidates) return [];
    return matchResult.candidates.filter(c => {
      if (dismissed.has(pairKey(c.item_id || c.best_item_id, c.doc_id))) return false;
      if (c.fuzzyScore !== null && c.fuzzyScore < fuzzyThreshold) return false;
      return true;
    });
  }, [matchResult, dismissed, fuzzyThreshold]);

  const candidatesByItem = useMemo(() => {
    const map = {};
    for (const c of visibleCandidates) {
      const itemId = c.item_id || c.best_item_id;
      if (!map[itemId]) map[itemId] = [];
      map[itemId].push(c);
    }
    return map;
  }, [visibleCandidates]);

  const totalCandidateItems = Object.keys(candidatesByItem).length;

  // ── Detect duplicate documents among visible (filtered) candidates ──
  const dupCandidateInfo = useMemo(() => {
    if (visibleCandidates.length === 0) return null;
    const hashCounts = {};
    for (const c of visibleCandidates) {
      const h = c.doc_hash;
      if (!h) continue;
      hashCounts[h] = (hashCounts[h] || 0) + 1;
    }
    const dupHashes = Object.entries(hashCounts).filter(([, count]) => count > 1);
    if (dupHashes.length === 0) return null;
    const totalDupCandidates = dupHashes.reduce((s, [, n]) => s + n, 0);
    return { groupCount: dupHashes.length, totalDups: totalDupCandidates };
  }, [visibleCandidates]);

  // When filtering by candidates, build items from candidate data (client-side pagination)
  const [candidatePage, setCandidatePage] = useState(1);

  const candidateItemList = useMemo(() => {
    if (totalCandidateItems === 0) return [];
    const seen = new Set();
    const list = [];
    for (const c of visibleCandidates) {
      const itemId = c.item_id || c.best_item_id;
      if (seen.has(itemId)) continue;
      seen.add(itemId);
      const actualItem = items.find(it => it.id === itemId);
      list.push({
        id: itemId,
        label: actualItem?.label || c.item_label || c.best_item_label || '—',
        status: actualItem?.status || c.item_status || 'not_reviewed',
        manually_verified: !!actualItem?.manually_verified,
        requirement_type: actualItem?.requirement_type || null,
        file_path: actualItem?.file_path || null,
        source_document_id: actualItem?.source_document_id || null,
        match_confidence: actualItem?.match_confidence ?? null,
        match_method: actualItem?.match_method || null,
        row_data: actualItem?.row_data || null,
      });
    }
    return list;
  }, [visibleCandidates, totalCandidateItems, items]);

  const candidateTotalPages = Math.max(1, Math.ceil(candidateItemList.length / PER_PAGE));
  const candidatePageItems = useMemo(() => {
    const start = (candidatePage - 1) * PER_PAGE;
    return candidateItemList.slice(start, start + PER_PAGE);
  }, [candidateItemList, candidatePage]);

  // Reset candidate page when candidates change
  useEffect(() => { setCandidatePage(1); }, [matchResult]);

  const latestRunBucketByItem = useMemo(() => {
    const map = {};
    for (const s of matchResult?.auto_accepted_samples || []) map[s.item_id] = { key: 'auto', label: 'Suggested', sample: s };
    for (const s of matchResult?.needs_review_samples || []) map[s.item_id] = { key: 'review', label: 'Suggested', sample: s };
    for (const s of matchResult?.no_match_samples || []) map[s.item_id] = { key: 'none', label: 'No candidate', sample: s };
    for (const id of matchResult?.auto_accepted_item_ids || []) map[id] = map[id] || { key: 'auto', label: 'Suggested' };
    for (const id of matchResult?.needs_review_item_ids || []) map[id] = map[id] || { key: 'review', label: 'Suggested' };
    for (const id of matchResult?.no_match_item_ids || []) map[id] = map[id] || { key: 'none', label: 'No candidate' };
    return map;
  }, [matchResult]);

  const displayItems = useMemo(() => {
    let base = items;

    // Server now handles every filter axis (decision_states, item_types,
    // bands). The previous client-side runBandFilter is gone — it was
    // working from a sampled subset of items and didn't reconcile with
    // pagination, so big checklists showed only a handful of items
    // even when the pill count claimed thousands.

    // Hide just-resolved items so they don't keep showing up in the
    // unresolved buckets (Suggested / Ambiguous / Needs Review) after the
    // user takes action. But DO show them in terminal-state buckets
    // (Confirmed / N/A / No Match) and in "All" — the user explicitly
    // wants to see what they just decided.
    //
    // Multi-select: if the active set contains ANY terminal-state bucket
    // (confirmed / n_a / no_match), assume the user wants to see resolved
    // items. Only hide-resolved when the active set is composed purely of
    // unresolved-state pills (or is empty).
    const TERMINAL_BUCKETS = new Set(['confirmed', 'n_a', 'no_match']);
    const UNRESOLVED_BUCKETS = new Set(['suggested', 'ambiguous', 'needs_review', 'profile_review']);
    const hasTerminal = Array.from(activeBuckets).some(b => TERMINAL_BUCKETS.has(b));
    const allUnresolved = activeBuckets.size > 0
      && Array.from(activeBuckets).every(b => UNRESOLVED_BUCKETS.has(b));
    const bucketHidesResolved = !hasTerminal && allUnresolved;
    if (bucketHidesResolved && resolvedReviewItemIds.size > 0) {
      base = base.filter(it => !resolvedReviewItemIds.has(it.id));
    }

    return base;
  }, [items, latestRunBucketByItem, resolvedReviewItemIds, activeBuckets]);

  const selectedChecklist = useMemo(() => {
    if (!checklists.length) return null;
    return checklists.find(c => c.id === selectedChecklistId)
      || checklists.find(c => c.is_active)
      || checklists[0]
      || null;
  }, [checklists, selectedChecklistId]);

  const activeChecklist = useMemo(
    () => checklists.find(c => c.is_active) || null,
    [checklists]
  );

  // ── Data fetchers ──

  const fetchChecklists = useCallback(async () => {
    setChecklistsLoading(true);
    try {
      const qs = showArchived ? '?include_archived=true' : '';
      const data = await api.get(`/cases/${caseId}/checklists${qs}`);
      const list = data.checklists || [];
      const nextSelected = selectedChecklistId
        || data.active_checklist_id
        || list[0]?.id
        || '';
      setChecklists(list);
      setArchivedCount(data.archived_count || 0);
      setSelectedChecklistId(prev => prev || nextSelected);
    } catch {
      setChecklists([]);
      setArchivedCount(0);
    } finally {
      setChecklistsLoading(false);
    }
  }, [caseId, selectedChecklistId, showArchived]);

  const fetchMatchSettings = useCallback(async () => {
    if (!selectedChecklistId) return;
    try {
      const data = await api.get(`/cases/${caseId}/checklist/${selectedChecklistId}/settings`);
      if (data?.settings) setMatchSettings(data.settings);
    } catch (err) {
      console.warn('Match settings fetch failed:', err?.message);
    }
  }, [caseId, selectedChecklistId]);

  const updateMatchSettings = useCallback(async (patch) => {
    if (!selectedChecklistId) return;
    setMatchSettingsSaving(true);
    // Optimistic update for snappy UX
    setMatchSettings(prev => ({ ...prev, ...patch }));
    try {
      const data = await api.patch(
        `/cases/${caseId}/checklist/${selectedChecklistId}/settings`,
        patch,
      );
      if (data?.settings) setMatchSettings(data.settings);
    } catch (err) {
      console.warn('Match settings update failed:', err?.message);
      // Roll back on error
      fetchMatchSettings();
    } finally {
      setMatchSettingsSaving(false);
    }
  }, [caseId, selectedChecklistId, fetchMatchSettings]);

  useEffect(() => { fetchMatchSettings(); }, [fetchMatchSettings]);

  const fetchOverview = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedChecklistId) params.set('checklist_id', selectedChecklistId);
      const qs = params.toString();
      const data = await api.get(`/cases/${caseId}/checklist/overview${qs ? `?${qs}` : ''}`);
      setOverview(data);
    } catch {
      // non-fatal
    } finally {
      setOverviewLoading(false);
    }
    setStatsRefreshTick(t => t + 1);
  }, [caseId, selectedChecklistId]);

  const fetchItems = useCallback(async (p = page) => {
    setBrowserLoading(true);
    try {
      const params = new URLSearchParams({ page: p, per_page: PER_PAGE });
      if (selectedChecklistId) params.set('checklist_id', selectedChecklistId);
      if (decisionStatesList.length > 0) params.set('decision_states', decisionStatesList.join(','));
      if (filterSearch) params.set('search', filterSearch);
      if (filterItemTypes.size > 0) params.set('item_types', Array.from(filterItemTypes).join(','));
      if (bandsList.length > 0) params.set('bands', bandsList.join(','));
      if (scoreRange[0] > 0) params.set('score_min', (scoreRange[0] / 100).toFixed(2));
      if (scoreRange[1] < 100) params.set('score_max', (scoreRange[1] / 100).toFixed(2));
      const data = await api.get(`/cases/${caseId}/checklist/items?${params}`);
      setItems(data.items || []);
      setTotalPages(data.total_pages || 1);
      setTotalItems(data.total_items || 0);
      setPage(data.page || p);
      setSelected(new Set());
    } catch {
      setItems([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setBrowserLoading(false);
    }
  }, [caseId, page, selectedChecklistId, decisionStatesList, filterSearch, filterItemTypes, bandsList, scoreRange]);

  // Live cross-tab counts for the filter pills. Refetched whenever the
  // active filters change. Cheap (one GROUP BY plus a few small follow-ups).
  const fetchFilterCounts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedChecklistId) params.set('checklist_id', selectedChecklistId);
      if (decisionStatesList.length > 0) params.set('decision_states', decisionStatesList.join(','));
      if (filterItemTypes.size > 0) params.set('item_types', Array.from(filterItemTypes).join(','));
      if (bandsList.length > 0) params.set('bands', bandsList.join(','));
      if (scoreRange[0] > 0) params.set('score_min', (scoreRange[0] / 100).toFixed(2));
      if (scoreRange[1] < 100) params.set('score_max', (scoreRange[1] / 100).toFixed(2));
      if (filterSearch) params.set('search', filterSearch);
      const data = await api.get(`/cases/${caseId}/checklist/items/counts?${params}`);
      setFilterCounts(data);
    } catch {
      // non-fatal — pills fall back to the legacy overview-derived counts
      setFilterCounts(null);
    }
  }, [caseId, selectedChecklistId, decisionStatesList, filterItemTypes, filterSearch, bandsList, scoreRange]);

  // Reflex match-health probe — cheap deterministic read of the latest
  // match run. The SPA polls this after every match-run completion and
  // on mount. When `is_weak` is true the banner over the action bar
  // renders with an "Improve matches" CTA.
  const fetchMatchHealth = useCallback(async () => {
    if (!caseId) return;
    try {
      const qs = selectedChecklistId
        ? `?checklist_id=${encodeURIComponent(selectedChecklistId)}`
        : '';
      const data = await api.get(`/cases/${caseId}/checklist/match-health${qs}`);
      setMatchHealth(data);
    } catch {
      // Non-fatal — banner just stays hidden when probe fails.
      setMatchHealth(null);
    }
  }, [caseId, selectedChecklistId]);

  // One-click "Improve matches" action — runs the deterministic upgrade
  // behind the banner. Per the plan §2 + §4, the banner never fires this
  // on its own; the user clicks the button.
  const handleImproveMatches = useCallback(async () => {
    if (!caseId) return;
    setImproveLoading(true);
    try {
      const qs = selectedChecklistId
        ? `?checklist_id=${encodeURIComponent(selectedChecklistId)}`
        : '';
      const data = await api.post(
        `/cases/${caseId}/checklist/improve-matches${qs}`,
        {},
      );
      // After a successful normalize, the score landscape will only
      // update once the user re-runs the match. Refresh the probe so
      // the banner reflects post-improve state, then surface whether
      // a rerun is recommended.
      await fetchMatchHealth();
      // Surface the banner once after a successful improve run so the
      // user sees the result. Auto-dismisses on checklist change or
      // explicit dismiss.
      setRecentlyImproved(true);
      setImproveDismissed(false);
      if (data?.match_rerun_recommended) {
        // Don't auto-fire the run — that's the bright line in §2. Just
        // hint via the banner's updated `reason` plus a confirm prompt.
        if (confirm(
          'Done. Find matches again now to use the updated list?',
        )) {
          // Re-run defers to the existing user-initiated path.
          runAgentAutoMatchRef.current?.({});
        }
      } else if (data?.normalize?.error) {
        alert(`Improve matches: ${data.normalize.error}`);
      }
    } catch (err) {
      alert(`Improve matches failed: ${err?.message || err}`);
    } finally {
      setImproveLoading(false);
    }
  }, [caseId, selectedChecklistId, fetchMatchHealth]);

  // Orphan-documents fetch — runs whenever the view switches to 'orphans'
  // or the user changes case/checklist/the underlying items table.
  const fetchOrphans = useCallback(async () => {
    if (!caseId) return;
    setOrphanLoading(true);
    try {
      const qs = selectedChecklistId ? `?checklist_id=${encodeURIComponent(selectedChecklistId)}` : '';
      const data = await api.get(`/cases/${caseId}/checklist/match/orphan-documents${qs}`);
      setOrphanDocs(data);
    } catch (err) {
      setOrphanDocs({ error: err?.message || 'Failed to load orphan documents', documents: [], total: 0 });
    } finally {
      setOrphanLoading(false);
    }
  }, [caseId, selectedChecklistId]);

  // Initial load
  useEffect(() => { fetchChecklists(); }, [fetchChecklists]);
  useEffect(() => { fetchOverview(); }, [fetchOverview]);
  useEffect(() => { fetchItems(1); }, [caseId, selectedChecklistId, decisionStatesList, filterSearch, filterItemTypes, bandsList, scoreRange]); // eslint-disable-line
  useEffect(() => { fetchFilterCounts(); }, [caseId, selectedChecklistId, decisionStatesList, filterSearch, filterItemTypes, bandsList, scoreRange, statsRefreshTick]); // eslint-disable-line
  // Probe match health: on mount + after each match-run completes.
  // `agentActive` flips true→false at the end of a run; the dependency
  // triggers a refetch whenever that transition fires (plus when the
  // user switches checklists).
  useEffect(() => {
    if (agentActive) return; // wait for the run to finish
    fetchMatchHealth();
  }, [agentActive, fetchMatchHealth]);
  // Reset the local dismiss flag when the user switches checklists so
  // a fresh "weak" probe on a new checklist surfaces the banner again.
  useEffect(() => {
    setImproveDismissed(false);
    setRecentlyImproved(false);
  }, [selectedChecklistId]);
  // Mirror committed → drag whenever scoreRange is reset externally
  // (e.g. Reset filters / Reset score). Without this the slider handle
  // could lag the committed value if some other code path moved it.
  useEffect(() => { setScoreDragRange(scoreRange); }, [scoreRange]);

  // Click-outside close for the settings gear popover.
  useEffect(() => {
    if (!matchSettingsOpen) return;
    function handleClick(e) {
      if (matchSettingsRef.current && !matchSettingsRef.current.contains(e.target)) {
        setMatchSettingsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [matchSettingsOpen]);
  useEffect(() => {
    if (viewMode === 'orphans') fetchOrphans();
  }, [viewMode, fetchOrphans]);
  useEffect(() => {
    setSelected(new Set());
    setSelectedPairs(new Set());
    setDismissed(new Set());
    setMatchResult(null);
    setResolvedReviewItemIds(new Set());
    setDecisionFilter('all');
    setPage(1);
  }, [selectedChecklistId]);

  useEffect(() => {
    if (!matchResult || typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(lastMatchCacheKey, JSON.stringify(matchResult));
    } catch {
      // non-fatal
    }
  }, [lastMatchCacheKey, matchResult]);

  useEffect(() => {
    let cancelled = false;

    async function hydrateLastMatchRun() {
      if (typeof window !== 'undefined') {
        try {
          const cached = window.localStorage.getItem(lastMatchCacheKey);
          if (cached && !cancelled) {
            const parsed = JSON.parse(cached);
            if (parsed?.items_total !== undefined) {
              setMatchResult(parsed);
              setActiveStrategy(parsed.method || 'persisted');
            }
          }
        } catch {
          // non-fatal
        }
      }

      try {
        const params = new URLSearchParams();
        if (selectedChecklistId) params.set('checklist_id', selectedChecklistId);
        const qs = params.toString();
        const data = await api.get(`/cases/${caseId}/checklist/match/last${qs ? `?${qs}` : ''}`);
        if (cancelled || !data?.available || !data?.result) return;
        setMatchResult(data.result);
        setActiveStrategy(data.result.method || 'persisted');
      } catch {
        // non-fatal
      }
    }

    hydrateLastMatchRun();
    return () => { cancelled = true; };
  }, [caseId, selectedChecklistId, lastMatchCacheKey]);

  // Cross-tab refresh: agent tools in ExplorationTab mutated checklist data
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.caseId === caseId) { fetchOverview(); fetchItems(page); }
    };
    window.addEventListener('checklist-dirty', handler);
    return () => window.removeEventListener('checklist-dirty', handler);
  }, [caseId, fetchOverview, fetchItems, page]);

  // ── Match candidates ──

  const fetchCandidates = useCallback(async (method) => {
    setMatchLoading(true);
    setMatchError(null);
    setMatchResult(null);
    setResolvedReviewItemIds(new Set());
    setSelectedPairs(new Set());
    setAcceptResult(null);
    try {
      const params = new URLSearchParams({ method });
      if (method === 'fuzzy') params.set('threshold', fuzzyThreshold);
      if (method === 'subset') params.set('min_length', '4');
      if (selectedChecklistId) params.set('checklist_id', selectedChecklistId);
      params.set('limit', '500');
      const data = await api.post(`/cases/${caseId}/checklist/match/candidates?${params}`, {});
      setMatchResult(data);
    } catch (err) {
      setMatchError(err.message || 'Failed to fetch candidates');
    } finally {
      setMatchLoading(false);
    }
  }, [caseId, fuzzyThreshold, selectedChecklistId]);

  const handleStrategyClick = useCallback((key) => {
    setActiveStrategy(key);
    setDismissed(new Set());
    fetchCandidates(key);
  }, [fetchCandidates]);

  // ── Candidate selection ──

  const togglePair = useCallback((itemId, docId) => {
    const key = pairKey(itemId, docId);
    setSelectedPairs(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  const selectAllCandidates = useCallback(() => {
    if (selectedPairs.size === visibleCandidates.length) {
      setSelectedPairs(new Set());
    } else {
      setSelectedPairs(new Set(visibleCandidates.map(c =>
        pairKey(c.item_id || c.best_item_id, c.doc_id)
      )));
    }
  }, [visibleCandidates, selectedPairs]);

  const dismissPair = useCallback((itemId, docId) => {
    const key = pairKey(itemId, docId);
    setDismissed(prev => new Set(prev).add(key));
    setSelectedPairs(prev => { const next = new Set(prev); next.delete(key); return next; });
  }, []);

  const buildReviewFeedback = useCallback((decision, baseReasoning, fallbackReasonCode = '') => {
    const reasonCode = reviewFeedback.reasonCode || fallbackReasonCode || '';
    const reviewerNote = reviewFeedback.reviewerNote.trim();
    const reasoning = [
      baseReasoning,
      reasonCode ? `reason code: ${reasonCode}` : null,
      reviewerNote ? `reviewer note: ${reviewerNote}` : null,
    ].filter(Boolean).join(' — ');

    return {
      reasoning,
      notes: [
        reasonCode ? `Reason code: ${reasonCode}` : null,
        reviewerNote ? `Reviewer note: ${reviewerNote}` : null,
      ].filter(Boolean).join('\n') || null,
      rowData: {
        review_feedback: {
          decision,
          reason_code: reasonCode || null,
          reviewer_note: reviewerNote || null,
          captured_via: 'self_guided_checklist_review',
          captured_at: new Date().toISOString(),
        },
      },
    };
  }, [reviewFeedback]);

  const requiresReviewFeedback = useCallback(
    (decisionOrStatus) => ['no_match', 'n_a', 'reject'].includes(decisionOrStatus),
    [],
  );

  const queueReviewAction = useCallback((payload) => {
    setReviewFeedback(DEFAULT_REVIEW_FEEDBACK);
    setPendingReviewAction(payload);
  }, []);

  const updateLatestRunAfterDecision = useCallback((itemId, targetStatus, patch = {}) => {
    const currentItem = items.find(it => it.id === itemId) || null;
    const previousDecision = currentItem ? getDecisionState(currentItem, latestRunBucketByItem[itemId]) : null;

    setResolvedReviewItemIds(prevIds => {
      const nextIds = new Set(prevIds);
      nextIds.add(itemId);
      return nextIds;
    });

    setSelected(prev => {
      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });

    setItems(prevItems => prevItems.map(it => {
      if (it.id !== itemId) return it;
      const nextRowData = patch.row_data
        ? {
            ...(it.row_data || {}),
            ...patch.row_data,
            ...(patch.row_data?.match_bundle ? {
              match_bundle: {
                ...(it.row_data?.match_bundle || {}),
                ...patch.row_data.match_bundle,
              },
            } : {}),
          }
        : it.row_data;
      return {
        ...it,
        ...patch,
        status: targetStatus,
        row_data: nextRowData,
      };
    }));

    setOverview(prev => {
      if (!prev || !currentItem) return prev;

      const statusCounts = { ...(prev.status_counts || {}) };
      if (currentItem.status && currentItem.status !== targetStatus) {
        statusCounts[currentItem.status] = Math.max(0, (statusCounts[currentItem.status] || 0) - 1);
        statusCounts[targetStatus] = (statusCounts[targetStatus] || 0) + 1;
      }

      const nextItem = {
        ...currentItem,
        ...patch,
        status: targetStatus,
        manually_verified: patch.manually_verified ?? currentItem.manually_verified,
        source_document_id: Object.prototype.hasOwnProperty.call(patch, 'source_document_id') ? patch.source_document_id : currentItem.source_document_id,
        file_path: Object.prototype.hasOwnProperty.call(patch, 'file_path') ? patch.file_path : currentItem.file_path,
      };
      const nextDecision = getDecisionState(nextItem, null);

      let suggestedCount = prev.suggested_count || 0;
      let approvedCount = prev.approved_count || 0;
      let noSuggestionCount = prev.no_suggestion_count || 0;

      const dec = (state) => {
        if (state === 'suggested') suggestedCount = Math.max(0, suggestedCount - 1);
        if (state === 'confirmed') approvedCount = Math.max(0, approvedCount - 1);
        if (state === 'no_suggestion') noSuggestionCount = Math.max(0, noSuggestionCount - 1);
      };
      const inc = (state) => {
        if (state === 'suggested') suggestedCount += 1;
        if (state === 'confirmed') approvedCount += 1;
        if (state === 'no_suggestion') noSuggestionCount += 1;
      };

      if (previousDecision && previousDecision !== nextDecision) {
        dec(previousDecision);
        inc(nextDecision);
      }

      return {
        ...prev,
        status_counts: statusCounts,
        suggested_count: suggestedCount,
        approved_count: approvedCount,
        no_suggestion_count: noSuggestionCount,
      };
    });

    setMatchResult(prev => {
      if (!prev) return prev;
      const next = { ...prev };
      next.candidates = (prev.candidates || []).filter(c => (c.item_id || c.best_item_id) !== itemId);
      next.candidate_count = next.candidates.length;
      next.needs_review_item_ids = (prev.needs_review_item_ids || []).filter(id => id !== itemId);
      next.needs_review_samples = (prev.needs_review_samples || []).filter(s => s.item_id !== itemId);
      next.auto_accepted_item_ids = (prev.auto_accepted_item_ids || []).filter(id => id !== itemId);
      next.auto_accepted_samples = (prev.auto_accepted_samples || []).filter(s => s.item_id !== itemId);
      next.no_match_item_ids = (prev.no_match_item_ids || []).filter(id => id !== itemId);
      next.no_match_samples = (prev.no_match_samples || []).filter(s => s.item_id !== itemId);
      next.needs_review = next.needs_review_item_ids.length;
      next.auto_accepted = next.auto_accepted_item_ids.length;
      next.no_match = next.no_match_item_ids.length;
      return next;
    });
  }, [items, latestRunBucketByItem]);

  // ── Bulk update candidate matches ──

  const bulkUpdateCandidates = useCallback(async (keys, targetStatus = 'matched') => {
    setAccepting(true);
    setAcceptResult(null);
    try {
      const updates = [];
      const statusLabel = STATUSES.find(s => s.key === targetStatus)?.label || targetStatus;
      for (const key of keys) {
        const [itemId, docId] = key.split('::');
        const cand = matchResult?.candidates?.find(
          c => (c.item_id || c.best_item_id) === itemId && c.doc_id === docId
        );
        const currentItem = items.find(it => it.id === itemId);
        const update = {
          item_id: itemId,
          status: targetStatus,
          manually_verified: true,
        };
        if (targetStatus === 'matched') {
          const feedback = buildReviewFeedback('approve', `Approved via ${activeStrategy} match`, 'correct_match');
          update.confidence = cand?.confidence ?? cand?.score ?? 1.0;
          update.reasoning = feedback.reasoning;
          update.notes = feedback.notes;
          update.row_data = { ...(currentItem?.row_data || {}), ...feedback.rowData };
          update.source_document_id = docId;
          update.file_path = cand?.doc_filename || null;
        } else {
          const feedback = buildReviewFeedback(
            targetStatus === 'n_a' ? 'n_a' : 'no_match',
            `Marked ${statusLabel} via ${activeStrategy} match`,
            targetStatus === 'n_a' ? 'wrong_checklist_interpretation' : 'missing_candidate',
          );
          update.reasoning = feedback.reasoning;
          update.notes = feedback.notes;
          update.row_data = { ...(currentItem?.row_data || {}), ...feedback.rowData };
        }
        updates.push(update);
      }
      const BATCH_SIZE = 200;
      for (let i = 0; i < updates.length; i += BATCH_SIZE) {
        await api.patch(`/cases/${caseId}/checklist/items/batch`, {
          updates: updates.slice(i, i + BATCH_SIZE),
        });
      }
      setAcceptResult({ ok: true, count: updates.length, status: statusLabel });
      updates.forEach(update => {
        updateLatestRunAfterDecision(update.item_id, update.status, {
          manually_verified: update.manually_verified,
          source_document_id: update.source_document_id || null,
          file_path: update.file_path || null,
          match_confidence: update.confidence ?? null,
          match_method: update.match_method || activeStrategy || 'manual',
          row_data: update.row_data || null,
        });
      });
      setSelectedPairs(new Set());
      fetchOverview();
      fetchItems(page);
      return true;
    } catch (err) {
      setAcceptResult({ ok: false, error: err.message });
      return false;
    } finally {
      setAccepting(false);
    }
  }, [caseId, activeStrategy, fetchOverview, fetchItems, page, items, buildReviewFeedback, updateLatestRunAfterDecision]);

  const acceptSelected = useCallback(() => {
    if (selectedPairs.size === 0) return;
    if (requiresReviewFeedback(candidateBulkStatus)) {
      queueReviewAction({
        type: 'candidate-bulk',
        keys: Array.from(selectedPairs),
        targetStatus: candidateBulkStatus,
      });
      return;
    }
    bulkUpdateCandidates(selectedPairs, candidateBulkStatus);
  }, [selectedPairs, bulkUpdateCandidates, candidateBulkStatus, queueReviewAction, requiresReviewFeedback]);


  const acceptOne = useCallback(async (itemId, docId) => {
    await bulkUpdateCandidates(new Set([pairKey(itemId, docId)]), 'matched');
  }, [bulkUpdateCandidates]);

  const markOneStatus = useCallback(async (itemId, targetStatus) => {
    setAccepting(true);
    setAcceptResult(null);
    try {
      const isNA = targetStatus === 'n_a';
      const feedback = buildReviewFeedback(
        isNA ? 'n_a' : 'no_match',
        isNA ? 'Marked N/A during review' : 'Marked no match during review',
        isNA ? 'wrong_checklist_interpretation' : 'missing_candidate',
      );
      await api.patch(`/cases/${caseId}/checklist/items/batch`, {
        updates: [{
          item_id: itemId,
          status: targetStatus,
          manually_verified: true,
          reasoning: feedback.reasoning,
          notes: feedback.notes,
          row_data: {
            ...(items.find(it => it.id === itemId)?.row_data || {}),
            ...feedback.rowData,
          },
        }],
      });
      setAcceptResult({ ok: true, count: 1, status: isNA ? 'N/A' : 'No Match' });
      updateLatestRunAfterDecision(itemId, targetStatus, {
        manually_verified: true,
        source_document_id: null,
        file_path: null,
        row_data: {
          ...(items.find(it => it.id === itemId)?.row_data || {}),
          ...feedback.rowData,
        },
      });
      fetchOverview();
      fetchItems(page);
    } catch (err) {
      setAcceptResult({ ok: false, error: err.message || `Failed to mark ${targetStatus}` });
    } finally {
      setAccepting(false);
    }
  }, [caseId, fetchOverview, fetchItems, page, updateLatestRunAfterDecision, buildReviewFeedback, items]);

  const markOneNoMatch = useCallback(async (itemId) => {
    await markOneStatus(itemId, 'no_match');
  }, [markOneStatus]);

  const markOneNA = useCallback(async (itemId) => {
    await markOneStatus(itemId, 'n_a');
  }, [markOneStatus]);

  // Inventory Mode entry point: stash the item label so the inventory
  // tab can render the banner without a round-trip, then navigate.
  // Session-scoped (per Inventory Mode plan § 4 non-goal).
  const handleFindInInventory = useCallback((item) => {
    if (!item?.id) return;
    try {
      window.sessionStorage.setItem(
        `mode:matchChecklistItem:${item.id}`,
        JSON.stringify({
          itemLabel: item.label || item.description || '',
          itemDescription: item.description || '',
        }),
      );
    } catch {
      // Quota / private mode — banner falls back to a generic copy.
    }
    navigate(`/case/${caseId}/inventory?match_item=${item.id}`);
  }, [caseId, navigate]);

  const handleBundleDecision = useCallback(async (item, action, fallbackDocId = null, fallbackDocName = null) => {
    if (!item?.id) return;

    const bundle = item?.row_data?.match_bundle || {};
    const currentIncludedDocIds = Array.from(new Set(
      bundle.included_doc_ids || bundle.proposed_included_doc_ids || (fallbackDocId ? [fallbackDocId] : [])
    ));
    const candidateDocIds = Array.from(new Set([
      ...currentIncludedDocIds,
      ...(bundle.borderline_doc_ids || []),
      ...((matchResult?.candidates || [])
        .filter(c => (c.item_id || c.best_item_id) === item.id)
        .map(c => c.doc_id)
        .filter(Boolean)),
    ]));
    const selectedDocIds = Array.from(new Set(
      Array.from(selectedPairs)
        .map(key => key.split('::'))
        .filter(([itemId, docId]) => itemId === item.id && !!docId)
        .map(([, docId]) => docId)
    ));

    // Normalize legacy 'partial' to 'approve' — selecting docs and approving is the only flow now
    const effectiveAction = action === 'partial' ? 'approve' : action;

    const finalIncludedDocIds = (effectiveAction === 'reject' || effectiveAction === 'n_a')
      ? []
      : (selectedDocIds.length > 0 ? selectedDocIds : currentIncludedDocIds);
    const finalExcludedDocIds = (effectiveAction === 'reject' || effectiveAction === 'n_a')
      ? candidateDocIds
      : candidateDocIds.filter(docId => !finalIncludedDocIds.includes(docId));

    const primaryDocId = finalIncludedDocIds[0] || fallbackDocId || null;
    const primaryCandidate = (matchResult?.candidates || []).find(
      c => (c.item_id || c.best_item_id) === item.id && c.doc_id === primaryDocId
    );
    const targetStatus = effectiveAction === 'reject' ? 'no_match' : effectiveAction === 'n_a' ? 'n_a' : 'matched';
    const sourceDocumentId = (effectiveAction === 'reject' || effectiveAction === 'n_a') ? null : primaryDocId;
    const sourceFilePath = (effectiveAction === 'reject' || effectiveAction === 'n_a')
      ? null
      : (item.file_path || primaryCandidate?.doc_filename || fallbackDocName || null);
    const reasonByAction = {
      approve: selectedDocIds.length > 0
        ? `Approved ${finalIncludedDocIds.length} selected bundle document${finalIncludedDocIds.length === 1 ? '' : 's'}`
        : 'Approved suggested bundle during grouped review',
      reject: 'Rejected bundle during grouped review',
      n_a: 'Marked bundle N/A during grouped review',
    };
    const reviewFeedbackPayload = buildReviewFeedback(
      effectiveAction === 'approve' ? 'approve' : effectiveAction === 'n_a' ? 'n_a' : 'reject',
      reasonByAction[effectiveAction] || 'Updated bundle decision during review',
      effectiveAction === 'reject' ? 'too_broad' : effectiveAction === 'n_a' ? 'wrong_checklist_interpretation' : 'correct_match',
    );

    setAccepting(true);
    setAcceptResult(null);
    try {
      await api.patch(`/cases/${caseId}/checklist/items/batch`, {
        updates: [{
          item_id: item.id,
          status: targetStatus,
          manually_verified: true,
          confidence: item.match_confidence ?? latestRunBucketByItem[item.id]?.sample?.score ?? 0.0,
          reasoning: reviewFeedbackPayload.reasoning,
          notes: reviewFeedbackPayload.notes || reasonByAction[effectiveAction] || null,
          match_method: `bundle/${effectiveAction}`,
          source_document_id: sourceDocumentId,
          file_path: sourceFilePath,
          row_data: {
            ...(item.row_data || {}),
            ...reviewFeedbackPayload.rowData,
            match_bundle: {
              ...(item.row_data?.match_bundle || {}),
              decision_action: effectiveAction,
              included_doc_ids: finalIncludedDocIds,
              excluded_doc_ids: finalExcludedDocIds,
              notes: reviewFeedbackPayload.notes || reasonByAction[effectiveAction] || null,
            },
          },
        }],
      });

      setItems(prevItems => prevItems.map(it => {
        if (it.id !== item.id) return it;
        return {
          ...it,
          status: targetStatus,
          source_document_id: sourceDocumentId,
          file_path: sourceFilePath,
          row_data: {
            ...(it.row_data || {}),
            ...reviewFeedbackPayload.rowData,
            match_bundle: {
              ...(it.row_data?.match_bundle || {}),
              decision_action: effectiveAction,
              included_doc_ids: finalIncludedDocIds,
              excluded_doc_ids: finalExcludedDocIds,
              notes: reviewFeedbackPayload.notes || reasonByAction[effectiveAction] || null,
            },
          },
        };
      }));

      setAcceptResult({
        ok: true,
        count: 1,
        status: effectiveAction === 'approve'
          ? 'Bundle Saved'
          : effectiveAction === 'n_a'
            ? 'Bundle Marked N/A'
            : 'Bundle Rejected',
      });
      updateLatestRunAfterDecision(item.id, targetStatus, {
        manually_verified: true,
        source_document_id: sourceDocumentId,
        file_path: sourceFilePath,
        match_confidence: item.match_confidence ?? latestRunBucketByItem[item.id]?.sample?.score ?? 0.0,
        match_method: `bundle/${effectiveAction}`,
        row_data: {
          ...(item.row_data || {}),
          ...reviewFeedbackPayload.rowData,
          match_bundle: {
            ...(item.row_data?.match_bundle || {}),
            decision_action: effectiveAction,
            included_doc_ids: finalIncludedDocIds,
            excluded_doc_ids: finalExcludedDocIds,
            notes: reviewFeedbackPayload.notes || reasonByAction[effectiveAction] || null,
          },
        },
      });
      fetchOverview();
      fetchItems(page);
    } catch (err) {
      setAcceptResult({ ok: false, error: err.message || 'Failed to apply bundle decision' });
    } finally {
      setAccepting(false);
    }
  }, [caseId, latestRunBucketByItem, matchResult, selectedPairs, updateLatestRunAfterDecision, fetchOverview, fetchItems, page, buildReviewFeedback]);

  const confirmPendingReviewAction = useCallback(async () => {
    if (!pendingReviewAction) return;

    const pending = pendingReviewAction;
    setPendingReviewAction(null);

    try {
      if (pending.type === 'bundle') {
        await handleBundleDecision(pending.item, pending.action, pending.docId, pending.docName);
      } else if (pending.type === 'candidate-bulk') {
        await bulkUpdateCandidates(new Set(pending.keys || []), pending.targetStatus);
      } else if (pending.type === 'item-bulk') {
        const statusLabel = STATUSES.find(s => s.key === pending.targetStatus)?.label || pending.targetStatus;
        setBulkApplying(true);
        try {
          const updates = (pending.itemIds || []).map((id) => {
            const currentItem = items.find(it => it.id === id);
            const feedback = buildReviewFeedback(
              pending.targetStatus === 'n_a' ? 'n_a' : 'no_match',
              `Marked ${statusLabel} during manual review`,
              pending.targetStatus === 'n_a' ? 'wrong_checklist_interpretation' : 'missing_candidate',
            );
            return {
              item_id: id,
              status: pending.targetStatus,
              manually_verified: true,
              reasoning: feedback.reasoning,
              notes: feedback.notes,
              row_data: {
                ...(currentItem?.row_data || {}),
                ...feedback.rowData,
              },
            };
          });
          await api.patch(`/cases/${caseId}/checklist/items/batch`, { updates });
          updates.forEach((update) => updateLatestRunAfterDecision(update.item_id, update.status, {
            manually_verified: true,
            source_document_id: null,
            file_path: null,
            row_data: update.row_data,
          }));
          setSelected(new Set());
          fetchOverview();
          fetchItems(page);
        } finally {
          setBulkApplying(false);
        }
      } else if (pending.action === 'n_a') {
        await markOneNA(pending.item.id);
      } else if (pending.action === 'approve') {
        await acceptOne(pending.item.id, pending.docId);
      } else {
        await markOneNoMatch(pending.item.id);
      }
    } finally {
      setReviewFeedback(DEFAULT_REVIEW_FEEDBACK);
    }
  }, [pendingReviewAction, handleBundleDecision, bulkUpdateCandidates, buildReviewFeedback, caseId, items, updateLatestRunAfterDecision, fetchOverview, fetchItems, page, markOneNA, acceptOne, markOneNoMatch]);

  // ── Agent auto-match (event-stream wiring) ──

  const matchResultRef = useRef(matchResult);
  useEffect(() => { matchResultRef.current = matchResult; }, [matchResult]);
  const stoppedByUserRef = useRef(false);
  // Captured from a typed `match_not_ready` event so the failure banner can
  // show a specific, actionable message (which items/docs couldn't be prepped)
  // instead of the generic fallback. Cleared on each fresh run / reset.
  const matchNotReadyRef = useRef(null);

  const handleMatchEvents = useCallback((events) => {
    // Snapshot at entry. handleReset bumps this; if the user reset
    // mid-stream, abandon the rest of this batch instead of letting it
    // re-set matchResult / progress / etc.
    const epochAtEntry = resetEpochRef.current;
    for (const ev of events) {
      if (resetEpochRef.current !== epochAtEntry) return;
      const evType = ev.event_type || ev.type;
      const data = ev.data || {};
      if (evType === 'profile_progress') {
        setProfileProgress(data.message || null);
      } else if (evType === 'profile_done') {
        setProfileActive(false);
        setProfileProgress(null);
        setProfileResult(data);
        // Refresh server-derived counters (overview.unprofiled_count
        // drives the "All N profiled" badge) and the items table now
        // that classifications have changed.
        fetchOverview();
        fetchItems(page);
        // Chain to match if the user clicked the unified "Find matches"
        // button while items were un-profiled. The ref is set in
        // handleFindMatches and cleared here whether or not we fire.
        if (pendingMatchAfterProfileRef.current) {
          pendingMatchAfterProfileRef.current = false;
          // Fire on a microtask so React state from setProfileActive(false)
          // settles before runAgentAutoMatch reads it.
          Promise.resolve().then(() => {
            runAgentAutoMatchRef.current?.({ skipDecided: false });
          });
        }
      } else if (evType === 'match_progress' || evType === 'progress') {
        const msg = data.message || data.summary;
        if (msg) {
          setAgentProgress(msg);
          setAgentActivity(prev => [...prev.slice(-29), { type: 'progress', message: msg, ts: Date.now() }]);
        }
      } else if (evType === 'match_summary') {
        if (data.iteration !== undefined) setMatchIteration(data.iteration);
      } else if (evType === 'match_cost_checkpoint') {
        setCostCheckpoint(data);
      } else if (evType === 'match_iteration_cap_reached') {
        setIterationCapReached(true);
      } else if (evType === 'user_question') {
        setAgentQuestion({
          question: data.question || '',
          context: data.context || null,
          askedAt: Date.now(),
        });
        setAgentAnswerDraft('');
        setGraphPaused(true);
        setAgentActive(false);
        setAgentProgress(null);
      } else if (evType === 'match_ready') {
        const suggested = data.suggested || [];
        const noMatch = data.no_match || [];
        setMatchResult({
          needs_review_item_ids: suggested.map(s => s.item_id),
          no_match_item_ids: noMatch.map(n => n.item_id),
          auto_accepted_item_ids: [],
          needs_review_samples: suggested,
          no_match_samples: noMatch,
          auto_accepted_samples: [],
          candidates: suggested.flatMap(s =>
            (s.included || []).map(doc => ({
              item_id: s.item_id,
              doc_id: doc.doc_id,
              confidence: doc.score ?? null,
              fuzzyScore: null,
              item_label: s.label,
              doc_filename: doc.doc_filename,
            }))
          ),
          summary: data.summary,
          checklist_id: data.checklist_id,
        });
        // Reset per-item decisions for fresh review
        setPerItemDecisions({});
        setPerItemNotes({});
        setSessionNarrative('');
        setCostCheckpoint(null);
        if (data.diagnostics?.iteration !== undefined) setMatchIteration(data.diagnostics.iteration);
        setActiveStrategy('unified');
        setGraphPaused(true);
        setAgentActive(false);
        setAgentProgress(null);
        // Re-fetch items so freshly-cleared profile_dirty flags drop the
        // staleness banner — the match graph clears profile_dirty for
        // items in scope at the end of the propose phase.
        fetchOverview();
        fetchItems(page);
      } else if (evType === 'match_apply_progress') {
        const msg = data.message || 'Applying…';
        setAgentProgress(msg);
        setAgentActivity(prev => [...prev.slice(-29), { type: 'progress', message: msg, ts: Date.now() }]);
      } else if (evType === 'match_apply_complete') {
        setGraphPaused(false);
        setAgentActive(false);
        setAgentProgress(null);
        fetchOverview();
        fetchItems(1);
      } else if (evType === 'tool_call') {
        const toolName = data.tool_name || data.tool || data.name;
        if (toolName) {
          const argsSummary = data.args_summary;
          let label = `Calling ${toolName}`;
          if (argsSummary && typeof argsSummary === 'object') {
            const keys = Object.keys(argsSummary);
            if (keys.length > 0) {
              const preview = keys.slice(0, 3).map(k => `${k}=${argsSummary[k]}`).join(', ');
              label = `${toolName}(${preview}${keys.length > 3 ? ', …' : ''})`;
            }
          }
          setAgentActivity(prev => [...prev.slice(-29), { type: 'tool_call', message: label, ts: Date.now() }]);
        }
      } else if (evType === 'match_not_ready') {
        // Readiness couldn't be healed server-side. Stash the specific
        // message; the status handler renders it when the run ends.
        matchNotReadyRef.current = data.message || null;
        setAgentActivity(prev => [...prev.slice(-29), {
          type: 'warning', message: data.message || 'Some items or documents could not be prepared for matching.', ts: Date.now(),
        }]);
      } else if (evType === 'match_warning') {
        const msg = data.message || 'Warning from match run';
        setAgentActivity(prev => [...prev.slice(-29), { type: 'warning', message: msg, ts: Date.now() }]);
        if (data.type === 'stopped_by_user' || data.type === 'cost_cap_hit') {
          if (data.type === 'stopped_by_user') stoppedByUserRef.current = true;
          setAgentActive(false);
          setAgentProgress(null);
        }
      } else if (evType === 'tool_result') {
        const toolName = data.tool_name || data.tool || data.name;
        if (toolName) {
          const summary = data.summary || (data.ok === false ? 'failed' : 'done');
          const durMs = data.duration_ms;
          const dur = typeof durMs === 'number' ? ` (${durMs}ms)` : '';
          setAgentActivity(prev => [...prev.slice(-29), {
            type: 'tool_result', message: `${toolName} → ${summary}${dur}`, ts: Date.now(),
          }]);
        }
      }
    }
  }, [fetchOverview, fetchItems]);

  const handleMatchStatusChange = useCallback((status) => {
    if (status === 'idle') {
      // Graph paused — match_ready event already handled state
      setAgentActive(false);
      setAgentProgress(null);
    } else if (status === 'completed') {
      setAgentActive(false);
      setAgentProgress(null);
      setGraphPaused(false);
      fetchOverview();
      fetchItems(1);
    } else if (status === 'failed' || status === 'interrupted') {
      setAgentActive(false);
      setAgentProgress(null);
      setGraphPaused(false);
      if (!matchResultRef.current && !stoppedByUserRef.current) {
        setMatchError(matchNotReadyRef.current || 'Matching run failed');
      }
    }
  }, [fetchOverview, fetchItems]);

  const chatEvents = useChatEvents(caseId, lastConversationId, {
    getToken: () => token,
    onEvents: handleMatchEvents,
    onStatusChange: handleMatchStatusChange,
    intervalMs: 500,
  });
  const chatEventsRef = useRef(chatEvents);
  useEffect(() => { chatEventsRef.current = chatEvents; }, [chatEvents]);

  // Resume through the graph's apply node
  const resumeWithApproval = useCallback(async (userInput) => {
    if (!lastConversationId) return;
    setAgentActive(true);
    setGraphPaused(false);
    setAgentProgress('Applying suggestions…');
    try {
      await api.post('/agent/resume', {
        conversation_id: lastConversationId,
        case_id: caseId,
        user_input: userInput,
      });
      chatEventsRef.current.start(0, lastConversationId);
    } catch (err) {
      setAgentActive(false);
      setGraphPaused(true);
      setAgentProgress(null);
      setMatchError(err.message || 'Failed to apply suggestions');
    }
  }, [caseId, lastConversationId]);

  // Send the reviewer's answer back to a paused agent ask_user call
  const handleSendAnswer = useCallback(() => {
    const answer = agentAnswerDraft.trim();
    if (!answer) return;
    setAgentQuestion(null);
    setAgentAnswerDraft('');
    resumeWithApproval({ action: 'answer', answer });
  }, [agentAnswerDraft, resumeWithApproval]);

  // Phase B: apply only the reviewer-approved subset
  const handleApplyApproved = useCallback(() => {
    const approvedIds = Object.entries(perItemDecisions)
      .filter(([, d]) => d === 'approve').map(([id]) => id);
    const rejectedIds = Object.entries(perItemDecisions)
      .filter(([, d]) => d === 'reject' || d === 'n_a').map(([id]) => id);
    // If reviewer made no per-item decisions, approve all suggested
    const allSuggested = (matchResult?.needs_review_samples || []).map(s => s.item_id);
    resumeWithApproval({
      action: 'approve',
      approved_item_ids: approvedIds.length > 0 ? approvedIds : allSuggested,
      rejected_item_ids: rejectedIds,
    });
  }, [perItemDecisions, matchResult, resumeWithApproval]);

  // Phase C: apply approved + re-run agent on remaining
  const handleRerunRemaining = useCallback(() => {
    const approvedIds = Object.entries(perItemDecisions)
      .filter(([, d]) => d === 'approve').map(([id]) => id);
    const rejectedItemsPayload = Object.entries(perItemDecisions)
      .filter(([, d]) => d === 'reject')
      .map(([id]) => ({
        item_id: id,
        reason_code: perItemNotes[id]?.reason_code || '',
        note: perItemNotes[id]?.note || '',
      }));
    setAgentProgress('Re-running on remaining items…');
    resumeWithApproval({
      action: 'refine',
      approved_item_ids: approvedIds,
      rejected_items: rejectedItemsPayload,
      narrative: sessionNarrative,
    });
  }, [perItemDecisions, perItemNotes, sessionNarrative, resumeWithApproval]);

  const acceptAllCandidates = useCallback(() => {
    if (visibleCandidates.length === 0) return;

    // If the graph is paused at the review step, route through /agent/resume
    // so the graph's apply node writes MATCHED status and emits apply events.
    if (graphPaused) {
      handleApplyApproved();
      return;
    }

    // Ad-hoc approval outside an active run — use the batch endpoint directly.
    const bestByItem = {};
    for (const c of visibleCandidates) {
      const itemId = c.item_id || c.best_item_id;
      const score = c.confidence ?? c.score ?? 0;
      if (!bestByItem[itemId] || score > (bestByItem[itemId].confidence ?? bestByItem[itemId].score ?? 0)) {
        bestByItem[itemId] = c;
      }
    }
    const bestKeys = new Set(Object.values(bestByItem).map(c =>
      pairKey(c.item_id || c.best_item_id, c.doc_id)
    ));
    if (requiresReviewFeedback(candidateBulkStatus)) {
      queueReviewAction({
        type: 'candidate-bulk',
        keys: Array.from(bestKeys),
        targetStatus: candidateBulkStatus,
      });
      return;
    }
    bulkUpdateCandidates(bestKeys, candidateBulkStatus);
  }, [visibleCandidates, bulkUpdateCandidates, candidateBulkStatus, queueReviewAction, requiresReviewFeedback, graphPaused, handleApplyApproved]);

  const handleStopMatch = useCallback(async () => {
    try {
      await api.post(`/cases/${caseId}/checklist/match/stop`, {});
    } catch (err) {
      console.warn('Stop request failed:', err);
    }
  }, [caseId]);

  const handleStopProfile = useCallback(async () => {
    try {
      await api.post(`/cases/${caseId}/checklist/profile/stop`, {});
      setProfileProgress('Stop requested — halting at the next batch boundary…');
    } catch (err) {
      console.warn('Profile stop request failed:', err);
    }
  }, [caseId]);

  // Forward-ref for runAgentAutoMatch — handleImproveMatches lives
  // earlier in the file but needs to call runAgentAutoMatch when the
  // user opts to re-run after an improve. Without the ref we'd hit a
  // TDZ / circular-callback issue.
  const runAgentAutoMatchRef = useRef(null);

  const runAgentAutoMatch = useCallback(async ({ itemIds = null } = {}) => {
    stoppedByUserRef.current = false;
    matchNotReadyRef.current = null;
    setAgentActive(true);
    setAgentProgress(`Starting match run for ${selectedChecklist?.name || 'selected checklist'}…`);
    setAgentActivity([]);
    setMatchError(null);
    setMatchResult(null);
    setGraphPaused(false);
    setIterationCapReached(false);
    setCostCheckpoint(null);
    setMatchIteration(0);
    setAgentQuestion(null);
    setAgentAnswerDraft('');
    try {
      const startData = await api.post(`/cases/${caseId}/checklist/match/unified`, {
        checklist_id: selectedChecklistId || undefined,
        item_ids: itemIds && itemIds.length > 0 ? itemIds : undefined,
      });
      const convId = startData.conversation_id;
      setLastConversationId(convId || null);
      if (convId) chatEventsRef.current.start(0, convId);
    } catch (err) {
      setMatchError(err.message);
      setAgentProgress(null);
      setAgentActive(false);
    }
  }, [caseId, selectedChecklist, selectedChecklistId]);

  // Keep the ref in sync so handleImproveMatches (declared earlier in the
  // component) can re-trigger a match run via the canonical user-initiated
  // path without circular hoisting.
  useEffect(() => {
    runAgentAutoMatchRef.current = runAgentAutoMatch;
  }, [runAgentAutoMatch]);

  const handleTypeOverride = useCallback(async (itemId, newType) => {
    const result = await api.patch(
      `/cases/${caseId}/checklist/items/${itemId}/item_type`,
      { item_type: newType },
    );
    fetchItems(page);
    return result;
  }, [caseId, page, fetchItems]);

  // Unified "Find matches" handler. The user shouldn't have to know
  // about the profile/match split — that's an internal pipeline shape,
  // not a workflow boundary. When the checklist has un-profiled items
  // we kick off profile first; profile_done auto-chains to match
  // (see the chained `pendingMatchAfterProfileRef` branch above).
  const handleFindMatches = useCallback(async ({ skipDecided = false } = {}) => {
    const unprofiledCount = overview?.unprofiled_count || 0;
    if (unprofiledCount > 0 && !profileActive) {
      pendingMatchAfterProfileRef.current = true;
      await runProfileChecklistRef.current?.({ onlyUnprofiled: true });
      return;
    }
    runAgentAutoMatchRef.current?.({ skipDecided });
  }, [overview, profileActive]);
  const runProfileChecklistRef = useRef(null);

  const runProfileChecklist = useCallback(async ({ onlyUnprofiled = true } = {}) => {
    setProfileActive(true);
    setProfileProgress(onlyUnprofiled ? 'Starting profile run (unprofiled items only)…' : 'Starting profile run (all items)…');
    setProfileResult(null);
    try {
      const params = new URLSearchParams();
      if (selectedChecklistId) params.set('checklist_id', selectedChecklistId);
      if (onlyUnprofiled) params.set('only_unprofiled', 'true');
      const qs = params.toString();
      const startData = await api.post(`/cases/${caseId}/checklist/profile${qs ? `?${qs}` : ''}`, {});
      const convId = startData.conversation_id;
      setLastConversationId(convId || null);
      if (convId) chatEventsRef.current.start(0, convId);
    } catch (err) {
      setProfileActive(false);
      setProfileProgress(`Profile failed: ${err.message}`);
      // Failed to even start profile — clear the chain intent so the
      // user can click again without a stale auto-fire pending.
      pendingMatchAfterProfileRef.current = false;
    }
  }, [caseId, selectedChecklistId]);
  useEffect(() => {
    runProfileChecklistRef.current = runProfileChecklist;
  }, [runProfileChecklist]);

  // Auto-resume active run on mount (survives page refresh)
  useEffect(() => {
    let cancelled = false;
    const applyResume = (data) => {
      if (cancelled || !data?.active) return;
      const convId = data.conversation_id;
      setLastConversationId(convId);
      setAgentActive(data.status === 'streaming');
      setGraphPaused(data.status === 'idle');
      setAgentProgress(data.status === 'streaming' ? 'Resuming…' : null);
      if (convId) chatEventsRef.current.start(0, convId);
    };

    // Prefer the dashboard seed; fall back to a direct fetch only if
    // the SPA arrived here without one (e.g. deep link refresh while
    // CaseDetail's dashboard fetch failed).
    if (caseDashboard?.active_checklist_match) {
      applyResume(caseDashboard.active_checklist_match);
    } else {
      (async () => {
        try {
          const data = await api.get(`/cases/${caseId}/checklist/match/active`);
          applyResume(data);
        } catch { /* non-fatal */ }
      })();
    }
    return () => { cancelled = true; };
    // caseDashboard intentionally not in deps — seed-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  // Auto-resume active checklist.profile run on mount. Mirror of the
  // match auto-resume above. Without this, navigating away mid-profile
  // leaves the progress UI dead even though the graph is still running
  // server-side. The SSE replay seeds the progress bar from the latest
  // emitted event.
  useEffect(() => {
    let cancelled = false;
    const applyResume = (data) => {
      if (cancelled || !data?.active) return;
      const convId = data.conversation_id;
      setLastConversationId(convId);
      setProfileActive(data.status === 'streaming');
      setProfileProgress(data.status === 'streaming' ? 'Reconnecting to in-progress profile run…' : null);
      if (convId) chatEventsRef.current.start(0, convId);
    };

    if (caseDashboard?.active_checklist_profile) {
      applyResume(caseDashboard.active_checklist_profile);
    } else {
      (async () => {
        try {
          const data = await api.get(`/cases/${caseId}/checklist/profile/active`);
          applyResume(data);
        } catch { /* non-fatal */ }
      })();
    }
    return () => { cancelled = true; };
    // caseDashboard intentionally not in deps — seed-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const handleActivateChecklist = useCallback(async () => {
    if (!selectedChecklistId || selectedChecklist?.is_active) return;
    setActivatingChecklist(true);
    try {
      await api.post(`/cases/${caseId}/checklists/${selectedChecklistId}/activate`, {});
      await fetchChecklists();
      fetchOverview();
      fetchItems(1);
    } catch {
      // non-fatal
    } finally {
      setActivatingChecklist(false);
    }
  }, [caseId, selectedChecklistId, selectedChecklist, fetchChecklists, fetchOverview, fetchItems]);

  const handleDeleteChecklist = useCallback(async () => {
    if (!selectedChecklistId || checklists.length <= 1) return;
    const name = selectedChecklist?.name || 'this checklist';
    if (!window.confirm(`Delete "${name}" and all its items? This cannot be undone.`)) return;
    setDeletingChecklist(true);
    try {
      await api.del(`/cases/${caseId}/checklists/${selectedChecklistId}`);
      setSelectedChecklistId('');
      await fetchChecklists();
      fetchOverview();
      fetchItems(1);
    } catch {
      // non-fatal
    } finally {
      setDeletingChecklist(false);
    }
  }, [caseId, selectedChecklistId, selectedChecklist, checklists.length, fetchChecklists, fetchOverview, fetchItems]);

  const handleReset = useCallback(async () => {
    setResetConfirm(false);
    try {
      // Bump the epoch FIRST, before any awaits. Any in-flight SSE
      // callback that fires while the await is pending will see the
      // bumped value and abandon its work.
      resetEpochRef.current += 1;
      const params = new URLSearchParams();
      if (selectedChecklistId) params.set('checklist_id', selectedChecklistId);
      await api.post(`/cases/${caseId}/checklist/reset${params.toString() ? `?${params}` : ''}`);
      // Detach SSE conversation so the polling hook stops requesting
      // events from the prior run.
      setLastConversationId(null);
      setMatchResult(null);
      setActiveStrategy(null);
      setAgentActivity([]);
      setAgentProgress(null);
      setAgentActive(false);
      setResolvedReviewItemIds(new Set());
      setDismissed(new Set());
      setSelectedPairs(new Set());
      setActiveBuckets(new Set());
      setFilterItemTypes(new Set());
      setScoreRange([0, 100]);
      try {
        if (typeof window !== 'undefined') window.localStorage.removeItem(lastMatchCacheKey);
      } catch {
        // non-fatal
      }
      // Authoritative re-hydrate. The reset endpoint now also drops
      // match_runs / match_candidates, so /match/last should return
      // available=false. We still call it explicitly so a yes-with-stale
      // result can't slip through the React effect timing.
      try {
        const lastQs = new URLSearchParams();
        if (selectedChecklistId) lastQs.set('checklist_id', selectedChecklistId);
        const data = await api.get(`/cases/${caseId}/checklist/match/last${lastQs.toString() ? `?${lastQs}` : ''}`);
        if (data?.available && data?.result) {
          // Server still has something — surface it so the UI is
          // truthful rather than silently masking the state.
          setMatchResult(data.result);
        } else {
          setMatchResult(null);
        }
      } catch {
        setMatchResult(null);
      }
      fetchOverview();
      fetchItems(1);
    } catch {
      // non-fatal
    }
  }, [caseId, fetchOverview, fetchItems, selectedChecklistId, lastMatchCacheKey]);

  // ── Export ──

  const handleExport = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedChecklistId) params.set('checklist_id', selectedChecklistId);
      // Match the same multi-select filter axes the items table is using.
      if (decisionStatesList.length > 0) params.set('decision_states', decisionStatesList.join(','));
      if (filterItemTypes.size > 0) params.set('item_types', Array.from(filterItemTypes).join(','));
      if (filterSearch) params.set('search', filterSearch);
      const data = await api.get(`/cases/${caseId}/checklist/export?${params}`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `checklist-${caseId}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // non-fatal
    }
  }, [caseId, selectedChecklistId, decisionStatesList, filterItemTypes, filterSearch]);

  // ── Bulk status update ──

  const handleBulkUpdate = useCallback(async () => {
    if (selected.size === 0) return;
    if (requiresReviewFeedback(bulkStatus)) {
      queueReviewAction({
        type: 'item-bulk',
        itemIds: Array.from(selected),
        targetStatus: bulkStatus,
      });
      return;
    }
    setBulkApplying(true);
    try {
      // manually_verified=true on matched/no_match so the row actually
      // leaves the suggestion bucket. Without this the persisted state is
      // status=matched + manually_verified=false, which getDecisionState
      // still treats as 'suggested'.
      const explicitStates = new Set(['matched', 'no_match', 'n_a']);
      const updates = Array.from(selected).map(id => ({
        item_id: id,
        status: bulkStatus,
        ...(explicitStates.has(bulkStatus) ? { manually_verified: true } : {}),
      }));
      await api.patch(`/cases/${caseId}/checklist/items/batch`, { updates });
      setSelected(new Set());
      fetchOverview();
      fetchItems(page);
    } catch {
      // non-fatal
    } finally {
      setBulkApplying(false);
    }
  }, [caseId, selected, bulkStatus, page, fetchOverview, fetchItems, queueReviewAction, requiresReviewFeedback]);

  // Bulk delete selected items. Used to clean up parser noise — comment
  // lines, headers, etc. that slipped in as fake items. Confirms first
  // because delete is irreversible.
  const handleBulkDelete = useCallback(async () => {
    if (selected.size === 0) return;
    const n = selected.size;
    const ok = window.confirm(
      `Delete ${n} checklist item${n === 1 ? '' : 's'}? This cannot be undone.`,
    );
    if (!ok) return;
    setBulkApplying(true);
    try {
      const ids = Array.from(selected);
      const body = { item_ids: ids };
      if (selectedChecklistId) body.checklist_id = selectedChecklistId;
      await api.del(`/cases/${caseId}/checklist/items/batch`, body);
      setSelected(new Set());
      fetchOverview();
      fetchItems(page);
    } catch {
      // non-fatal — error surfacing happens via the overview refetch
    } finally {
      setBulkApplying(false);
    }
  }, [caseId, selected, selectedChecklistId, page, fetchOverview, fetchItems]);

  // Confirm the top candidate for every selected item in one shot.
  // Only items that actually have a top candidate get confirmed — others are skipped silently.
  const handleBulkConfirmSuggested = useCallback(async () => {
    if (selected.size === 0) return;
    const pairs = new Set();
    for (const itemId of selected) {
      const topCand = candidatesByItem[itemId]?.[0];
      if (topCand?.doc_id) pairs.add(pairKey(itemId, topCand.doc_id));
    }
    if (pairs.size === 0) return;
    await bulkUpdateCandidates(pairs, 'matched');
    setSelected(new Set());
  }, [selected, candidatesByItem, bulkUpdateCandidates]);

  // ── Selection helpers ──

  const toggleSelect = useCallback((id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  // Select-all toggles every CURRENTLY VISIBLE row (i.e., what passes the
  // active filters), not every row in the case. That's the affordance the
  // reviewer wants: filter to "Suggested Matches" → Select all → bulk approve.
  const toggleSelectAll = useCallback(() => {
    const visibleIds = (displayItems || []).map(i => i.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selected.has(id));
    if (allVisibleSelected) {
      setSelected(prev => {
        const next = new Set(prev);
        visibleIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelected(prev => {
        const next = new Set(prev);
        visibleIds.forEach(id => next.add(id));
        return next;
      });
    }
  }, [displayItems, selected]);

  const hasChecklist = overview && overview.total_items > 0;
  const hasReverseLayout = matchResult?.method === 'reverse';
  const hasFiletypeLayout = matchResult?.method === 'filetype';

  if (overviewLoading || checklistsLoading) {
    return <div className="cl-loading">Loading checklist…</div>;
  }

  return (
    <div className="checklist-tab">
      {!hasChecklist && (
        <div className="cl-empty">
          <h3>Check what you received against what you were promised</h3>
          <p>
            Load the discovery index you were served (the list of everything the other
            side says they produced) and Arc checks every item against the files actually
            in this case — flagging anything missing, extra, or mislabeled.
          </p>
          <p>
            To get started: upload the index like any other file (CSV, XLSX, or PDF),
            then open it in <strong>Document Inventory</strong> and choose{' '}
            <strong>Use as Checklist</strong>. Already uploaded it? It may just need
            marking — find it in the inventory and use the same action.
          </p>
        </div>
      )}

      {hasChecklist && (
        <>
          <ProgressSection overview={overview} matchResult={matchResult} filterCounts={filterCounts} />

          {/* ── Unified checklist + matching section ── */}
          <div className="cl-section cl-unified">
            <div className="cl-section-header">
              <h3 style={{ margin: 0 }}>Checklist Items <span className="cl-count">({totalItems}{overview && totalItems !== overview.total_items ? ` of ${overview.total_items}` : ''})</span></h3>
              <div className="cl-checklist-selector-bar">
                <label htmlFor="checklist-selector" style={{ fontWeight: 500, color: '#374151' }}>Checklist:</label>
                <select
                  id="checklist-selector"
                  value={selectedChecklistId}
                  onChange={e => setSelectedChecklistId(e.target.value)}
                >
                  {checklists.map(cl => {
                    // Enriched label so a paralegal can tell duplicates
                    // apart at a glance: name • Active • N items • date.
                    // The date is the import time, formatted short.
                    const parts = [cl.name];
                    if (cl.archived) parts.push('Archived');
                    if (cl.is_active && !cl.archived) parts.push('Active');
                    if (cl.item_count != null) {
                      parts.push(`${cl.item_count} item${cl.item_count === 1 ? '' : 's'}`);
                    }
                    if (cl.created_at) {
                      const d = new Date(cl.created_at);
                      // e.g. "Jun 1, 9:30 PM" — calendar + time so same-day
                      // imports stay distinguishable.
                      parts.push(d.toLocaleString(undefined, {
                        month: 'short', day: 'numeric',
                        hour: 'numeric', minute: '2-digit',
                      }));
                    }
                    return (
                      <option key={cl.id} value={cl.id}>
                        {parts.join(' • ')}
                      </option>
                    );
                  })}
                </select>
                {(archivedCount > 0 || showArchived) && (
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    onClick={() => setShowArchived(v => !v)}
                    title={showArchived
                      ? 'Hide archived checklists from this menu'
                      : 'Show previously imported (archived) checklists'}
                    style={{ fontSize: '0.83rem' }}
                  >
                    {showArchived
                      ? '✕ Hide archived'
                      : `📦 Show archived (${archivedCount})`}
                  </button>
                )}
                {selectedChecklist && !selectedChecklist.is_active && (
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={handleActivateChecklist}
                    disabled={activatingChecklist}
                  >
                    {activatingChecklist ? '⏳ Setting Active…' : 'Make Active'}
                  </button>
                )}
                {checklists.length > 1 && (
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={handleDeleteChecklist}
                    disabled={deletingChecklist}
                    style={{ color: '#dc2626' }}
                    title="Delete this checklist and all its items"
                  >
                    {deletingChecklist ? '⏳ Deleting…' : 'Delete'}
                  </button>
                )}
                {selectedChecklist?.source && (
                  <span style={{ fontSize: '0.83rem', color: '#6b7280' }}>
                    Source: {selectedChecklist.source}
                  </span>
                )}
                {activeChecklist && activeChecklist.id !== selectedChecklist?.id && (
                  <span style={{ fontSize: '0.83rem', color: '#b45309' }}>
                    Active default: {activeChecklist.name}
                  </span>
                )}
              </div>
            </div>

            {(() => {
              const confirmed = overview?.approved_count || 0;
              const noMatch = overview?.status_counts?.no_match || 0;
              const na = overview?.status_counts?.n_a || 0;
              const decided = confirmed + noMatch + na;
              const total = overview?.total_items || 0;
              const remaining = Math.max(total - decided, 0);
              const hasDecisions = decided > 0;
              // Live profile progress — drives "Preparing… (X/Y)" on
              // the primary Find-matches button during a chained run.
              const liveMatch = (profileProgress || '').match(/(\d+)\/(\d+)/);
              const liveDone = liveMatch ? Number(liveMatch[1]) : null;
              const liveTotal = liveMatch ? Number(liveMatch[2]) : null;

              return (
                <>
                  {/* Compact action bar — single primary CTA + summary +
                      power-user chips (Profile menu, Match settings). The
                      unified Find-matches button transparently chains
                      profile → match so paralegals don't have to know that
                      "Profile" is a separate step. */}
                  <div className="cl-action-bar">
                    {(() => {
                      // Button visibility + copy reflects pipeline state.
                      // matchHealth.latest_run_id is the durable "has a
                      // prior run" signal — survives page refreshes unlike
                      // the in-memory matchResult. Re-run on the global
                      // checklist is only meaningful when:
                      //   - no prior run yet (first run), OR
                      //   - new docs have arrived since the last run.
                      // For "fix a single item profile" the per-item
                      // re-match action in the row drawer is the path.
                      const hasPriorRun = !!matchHealth?.latest_run_id;
                      const lastRunAt = matchHealth?.latest_run_at;
                      const newDocsSinceLastRun = (hasPriorRun && lastRunAt && Array.isArray(caseDocuments))
                        ? caseDocuments.filter(d => {
                            const ts = d.uploaded_at || d.created_at;
                            return ts && new Date(ts) > new Date(lastRunAt);
                          }).length
                        : 0;
                      // Hide the button entirely when there's a prior run
                      // and nothing changed since — re-running the matcher
                      // on identical inputs produces identical results and
                      // burns LLM cost for no informational gain.
                      const isInProgress = agentActive || profileActive;
                      const hideButton = hasPriorRun && newDocsSinceLastRun === 0 && !isInProgress;
                      if (hideButton) return null;
                      const buttonLabel = agentActive
                        ? <><ArcOrb size={14} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 5 }} />Finding matches…</>
                        : profileActive
                          ? (liveDone != null && liveTotal != null
                              ? `Preparing… (${liveDone}/${liveTotal})`
                              : 'Preparing…')
                          : hasPriorRun && newDocsSinceLastRun > 0 && remaining > 0
                            ? `Match ${newDocsSinceLastRun} new doc${newDocsSinceLastRun === 1 ? '' : 's'} against ${remaining} remaining item${remaining === 1 ? '' : 's'}`
                            : hasPriorRun && newDocsSinceLastRun > 0
                              ? `Match ${newDocsSinceLastRun} new doc${newDocsSinceLastRun === 1 ? '' : 's'}`
                              : 'Find matches';
                      return (
                        <button
                          className="btn btn-primary mw-auto-btn"
                          onClick={() => handleFindMatches({})}
                          disabled={agentActive || profileActive || matchLoading || !selectedChecklistId}
                        >
                          {buttonLabel}
                        </button>
                      );
                    })()}
                    {(agentActive || profileActive) && (
                      <button
                        className="btn btn-sm cl-stop-btn"
                        onClick={agentActive ? handleStopMatch : handleStopProfile}
                        title="Stop after the current step"
                      >
                        Stop
                      </button>
                    )}
                    {hasDecisions && !agentActive && (
                      <span className="cl-decision-summary">
                        {confirmed > 0 && <>{confirmed} confirmed &middot; </>}
                        {noMatch > 0 && <>{noMatch} no match &middot; </>}
                        {na > 0 && <>{na} N/A &middot; </>}
                        <em>{remaining} remaining</em>
                      </span>
                    )}

                    {/* Inline Improve chip — discoverable entry point to
                        the deterministic normalization pass. Hidden once
                        improve has been run (normalize_applied=true)
                        since the upgrade is a one-shot transformation;
                        re-clicking would no-op. Same "no informational
                        gain → hide" rule the Re-run button follows. The
                        full banner with the result only appears AFTER
                        the user clicks this (transient by design). */}
                    {selectedChecklistId
                      && !agentActive
                      && !profileActive
                      && (overview?.unprofiled_count || 0) === 0
                      && matchHealth?.is_weak
                      && !matchHealth?.normalize_applied
                      && !recentlyImproved && (
                      <button
                        type="button"
                        className="btn btn-sm cl-improve-chip"
                        onClick={handleImproveMatches}
                        disabled={improveLoading}
                        title="Run the deterministic normalization pass to surface more matches"
                      >
                        {improveLoading ? '⏳ Improving…' : '✨ Improve matches'}
                      </button>
                    )}

                    <div className="cl-action-bar-spacer" />

                    {/* Profile chip removed — its re-process action
                        moved into the gear menu below, and its status
                        indicator was redundant with the unified Find-
                        matches button (which shows Preparing… while
                        the chained profile pass is running). */}

                    {/* Settings gear — consolidates match settings +
                        the "re-profile all" power-user affordance that
                        used to live in its own chip. Lawyer-facing UI
                        only needs one settings icon. */}
                    {selectedChecklistId && (
                      <div className="hfilter-wrap" ref={matchSettingsRef}>
                        <button
                          type="button"
                          className={`filter-chip ${!matchSettings.exclusive_matching ? 'filter-chip--warn' : ''} ${matchSettingsOpen ? 'filter-chip--active' : ''}`}
                          onClick={() => setMatchSettingsOpen(o => !o)}
                          aria-expanded={matchSettingsOpen}
                          title="Settings"
                        >
                          ⚙
                          {!matchSettings.exclusive_matching && <span className="cl-chip-dot" />}
                        </button>
                        {matchSettingsOpen && (
                          <div className="hfilter-popover" style={{ minWidth: 320 }}>
                            <div className="hfilter-section" style={{ padding: '10px 12px' }}>
                              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={!!matchSettings.exclusive_matching}
                                  onChange={(e) => updateMatchSettings({ exclusive_matching: e.target.checked })}
                                  disabled={matchSettingsSaving || agentActive}
                                  style={{ marginTop: 3 }}
                                />
                                <span>
                                  <span style={{ fontWeight: 600 }}>Exclusive matching</span>
                                  <div style={{ fontSize: '0.83rem', color: '#666', marginTop: 2 }}>
                                    Each document can only be matched to one item. Once matched,
                                    the document is removed from candidate lists for every other
                                    item. Uncheck to allow a document to satisfy multiple
                                    checklist entries.
                                  </div>
                                </span>
                              </label>
                            </div>
                            {/* Re-profile all — power-user affordance.
                                Lives behind a confirm() because it's a
                                full-LLM-pass cost. */}
                            <div className="hfilter-section">
                              <button
                                type="button"
                                className="hfilter-option"
                                onClick={() => {
                                  if (confirm('Re-process all items in this checklist? This will re-run the AI classifier on items that have already been processed. Use this only if you intentionally want to refresh classifications.')) {
                                    setMatchSettingsOpen(false);
                                    runProfileChecklist({ onlyUnprofiled: false });
                                  }
                                }}
                                disabled={profileActive || agentActive}
                                title="Re-classify every item in this checklist."
                              >
                                <span>↻ Re-process all items</span>
                                <span className="hfilter-count">{total}</span>
                              </button>
                              {profileResult && !profileActive && (
                                <div className="hfilter-footer cl-popover-note" style={{ fontSize: '0.78rem', color: '#6b7280', padding: '6px 12px' }}>
                                  Last run: {profileResult.applied} processed
                                  {profileResult.needs_review > 0 && ` · ${profileResult.needs_review} flagged`}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* The "items need profiling first" banner was redundant
                      after Find-matches became a single button — clicking
                      it now transparently chains profile → match. Power-
                      user re-profile affordances still live in the Profile
                      menu chip on the right. */}

                  {/* Reflex match-health banner — transient. Hidden by
                      default. Surfaces once after the user clicks Improve
                      matches so they can see the result; auto-clears on
                      dismiss or checklist change. The "always-on weak"
                      banner was clutter (per 2026-06-01 UX feedback).
                      Discovery path is the inline "✨ Improve matches"
                      chip rendered below when matchHealth.is_weak. */}
                  {selectedChecklistId
                    && !agentActive
                    && !profileActive
                    && (overview?.unprofiled_count || 0) === 0
                    && recentlyImproved
                    && !improveDismissed && (
                    <MatchHealthBanner
                      health={matchHealth}
                      loading={improveLoading}
                      onImprove={handleImproveMatches}
                      onDismiss={() => {
                        setImproveDismissed(true);
                        setRecentlyImproved(false);
                      }}
                      totalItems={total}
                    />
                  )}
                </>
              );
            })()}

            {/* Cost checkpoint banner (Phase D) */}
            {costCheckpoint && !graphPaused && (
              <div className="mw-agent-feed" style={{ marginBottom: '12px', borderLeft: '3px solid #f59e0b' }}>
                <div className="mw-agent-header" style={{ justifyContent: 'space-between' }}>
                  <span>⏱ Matching progress</span>
                  <button className="btn btn-sm btn-ghost" style={{ marginLeft: 'auto' }} onClick={() => setCostCheckpoint(null)}>×</button>
                </div>
                <div style={{ fontSize: '0.92rem', color: '#555' }}>
                  {costCheckpoint.proposed_so_far} matched · {costCheckpoint.unresolved} remaining
                  · {costCheckpoint.tool_calls_used} of {costCheckpoint.tool_calls_budget} tool calls used
                </div>
              </div>
            )}

            {/* Agent ask_user panel — shown when agent paused mid-run with a question */}
            {graphPaused && agentQuestion && (
              <div className="mw-agent-feed" style={{ marginBottom: '12px', borderLeft: '3px solid #6366f1', padding: '12px' }}>
                <div className="mw-agent-header" style={{ marginBottom: '8px' }}>
                  <span>🙋 Agent has a question</span>
                </div>
                <div style={{ fontSize: '0.95rem', color: '#1f2937', marginBottom: '6px', fontWeight: 500 }}>
                  {agentQuestion.question}
                </div>
                {agentQuestion.context && (
                  <div style={{ fontSize: '0.86rem', color: '#6b7280', marginBottom: '10px', whiteSpace: 'pre-wrap' }}>
                    {agentQuestion.context}
                  </div>
                )}
                <textarea
                  value={agentAnswerDraft}
                  onChange={(e) => setAgentAnswerDraft(e.target.value)}
                  placeholder="Type your answer…"
                  rows={3}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db', fontFamily: 'inherit', fontSize: '0.92rem', marginBottom: '8px' }}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleSendAnswer}
                    disabled={!agentAnswerDraft.trim()}
                  >
                    Send answer
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      setAgentQuestion(null);
                      setAgentAnswerDraft('');
                      resumeWithApproval({ action: 'cancel' });
                    }}
                  >
                    Cancel run
                  </button>
                </div>
              </div>
            )}


            {/* Duplicate document warning banner */}
            {dupCandidateInfo && (
              <div className="mw-dedup-banner">
                <span className="mw-dedup-icon">⚠️</span>
                <span className="mw-dedup-text">
                  <strong>{dupCandidateInfo.totalDups} candidates</strong> point to duplicate documents
                  ({dupCandidateInfo.groupCount} groups with identical content).
                  Deduplicate in the <strong>Inventory</strong> tab first for cleaner results.
                </span>
              </div>
            )}

            {/* Agent activity feed */}
            {(agentActive || agentActivity.length > 0) && (
              <div className="mw-agent-feed">
                <div className="mw-agent-header">
                  {agentActive && <ArcOrb size={16} style={{ flexShrink: 0 }} />}
                  <span>{agentActive ? (agentProgress || 'Working…') : `${AGENT_NAME_SHORT} finished`}</span>
                </div>
                {agentActivity.length > 0 && (
                  <div className="mw-agent-log" ref={feedRef}>
                    {agentActivity.map((entry, i) => (
                      <div key={i} className={`mw-agent-entry mw-agent-${entry.type}`}>
                        <span className="mw-agent-icon">
                          {entry.type === 'tool_call' ? '🔧' : entry.type === 'tool_result' ? '✅' : '💭'}
                        </span>
                        <span className="mw-agent-msg">{entry.message}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Match error */}
            {matchError && <div className="mw-error">❌ {matchError}</div>}

            {/* Match loading */}
            {matchLoading && (
              <div className="mw-loading">
                <ArcOrb size={16} style={{ flexShrink: 0 }} />
                <span>Finding {activeStrategy} candidates…</span>
              </div>
            )}

            {/* Accept result banner */}
            {acceptResult && (
              <div className={`mw-accept-banner ${acceptResult.ok ? 'mw-accept-ok' : 'mw-accept-err'}`}>
                {acceptResult.ok ? `✅ ${acceptResult.count} items → ${acceptResult.status || 'Matched'}` : `❌ ${acceptResult.error}`}
                <button className="mw-dismiss-banner" onClick={() => setAcceptResult(null)}>×</button>
              </div>
            )}



            {/* Candidate summary + bulk actions — legacy candidate flow only.
                Hidden when the per-item review panel is active OR when the
                new deterministic pipeline produced results (any sample with
                a 'band' field is from the new pipeline; the bulk-approve
                button inside MatchReviewPanel replaces this row). */}
            {visibleCandidates.length > 0 && !hasFiletypeLayout && !graphPaused
              && !(matchResult?.needs_review_samples || []).some(s => s.band) && (
              <div className="cl-candidate-bar">
                <label className="cl-candidate-select-all" title="Select / deselect all candidates">
                  <input type="checkbox"
                    checked={selectedPairs.size > 0 && selectedPairs.size === visibleCandidates.length}
                    ref={el => { if (el) el.indeterminate = selectedPairs.size > 0 && selectedPairs.size < visibleCandidates.length; }}
                    onChange={selectAllCandidates} />
                </label>
                <span className="cl-candidate-summary">
                  {hasReverseLayout
                    ? `${matchResult?.unclaimed_count || 0} unclaimed docs — ${visibleCandidates.length} candidates`
                    : `${candidateItemList.length} items with matches`
                  }
                  {selectedPairs.size > 0 && ` (${selectedPairs.size} selected)`}
                </span>
                <select className="cl-candidate-action-select" value={candidateBulkStatus}
                  onChange={e => setCandidateBulkStatus(e.target.value)}>
                  {STATUSES.filter(s => s.key !== 'not_reviewed').map(s => <option key={s.key} value={s.key}>{s.icon} {s.label}</option>)}
                </select>
                <button className="btn btn-sm btn-primary" onClick={selectedPairs.size > 0 ? acceptSelected : acceptAllCandidates}
                  disabled={accepting}>
                  {accepting ? '⏳ Applying…'
                    : selectedPairs.size > 0
                      ? `Apply to ${selectedPairs.size} selected`
                      : `Apply to ${candidateItemList.length} items`}
                </button>
              </div>
            )}

            {/* Filetype grouped layout */}
            {hasFiletypeLayout && matchResult?.groups && (
              <div className="mw-filetype-groups">
                {matchResult.groups.map((group, gi) => (
                  <div key={gi} className="mw-filetype-group">
                    <div className="mw-filetype-header">
                      <strong>{group.category}</strong>
                      <span>{group.item_count} items • {group.doc_count} docs</span>
                    </div>
                    <div className="mw-filetype-cols">
                      <div className="mw-filetype-col">
                        <div className="mw-filetype-col-label">📋 Checklist Items</div>
                        {group.sample_items?.map((it, i) => (
                          <div key={i} className="mw-filetype-entry">{it.item_label}</div>
                        ))}
                        {group.item_count > 10 && <div className="mw-filetype-more">+{group.item_count - 10} more</div>}
                      </div>
                      <div className="mw-filetype-col">
                        <div className="mw-filetype-col-label">📄 Documents</div>
                        {group.sample_docs?.map((doc, i) => (
                          <div key={i} className="mw-filetype-entry">{doc.doc_filename}</div>
                        ))}
                        {group.doc_count > 10 && <div className="mw-filetype-more">+{group.doc_count - 10} more</div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Filters row — view selector + bucket pills + search */}
            <div className="cl-filters" style={{ flexWrap: 'wrap' }}>
              <select
                value={viewMode}
                onChange={e => setViewMode(e.target.value)}
                title="Switch between checklist items and unmatched documents"
                style={{ fontWeight: 500 }}
              >
                <option value="items">📋 Checklist items</option>
                <option value="orphans">📄 Unmatched documents{orphanDocs?.total != null ? ` (${orphanDocs.total})` : ''}</option>
              </select>
              {/* Unified filter popover — Status (buckets) + Type + Match score.
                  Replaces the prior row of inline bucket pills; mirrors the
                  document-inventory HierarchicalFilterChip pattern. */}
              {viewMode === 'items' && (() => {
                const dsCounts = filterCounts?.decision_state || {};
                const naCount = dsCounts.n_a ?? overview?.status_counts?.n_a ?? 0;
                // Paralegal-facing status buckets. Internal-only options
                // (Ambiguous band, Profile Review, zero-count N/A) are
                // pruned to match the row UI pass. The data model still
                // supports them; they just aren't surfaced as primary
                // filters. Power users can reach them via Ask Arc.
                // Filter buckets mirror the row decision pills 1:1 so
                // the filter taxonomy matches what's visible per row.
                // N/A is always shown (even when count=0) for taxonomy
                // consistency.
                const bucketOptions = [
                  { value: 'suggested',    label: 'Suggested',    icon: '🤖', count: dsCounts.suggested ?? 0 },
                  { value: 'confirmed',    label: 'Confirmed',    icon: '✅', count: dsCounts.confirmed ?? overview?.approved_count ?? 0 },
                  { value: 'needs_review', label: 'Needs Review', icon: '👀', count: dsCounts.no_suggestion ?? overview?.no_suggestion_count ?? 0 },
                  { value: 'no_match',     label: 'No Match',     icon: '❌', count: dsCounts.no_match ?? overview?.status_counts?.no_match ?? 0 },
                  { value: 'n_a',          label: 'N/A',          icon: '➖', count: naCount },
                ];
                return (
                  <ChecklistFilterPopover
                    activeBuckets={activeBuckets}
                    bucketOptions={bucketOptions}
                    onBucketToggle={(key) => toggleBucket(key)}
                    scoreRange={scoreRange}
                    scoreDragRange={scoreDragRange}
                    onScoreRangeChange={(v) => setScoreRange(v)}
                    onScoreDragChange={(v) => setScoreDragRange(v)}
                    onResetAll={() => {
                      setActiveBuckets(new Set());
                      setFilterItemTypes(new Set());
                      setScoreRange([0, 100]);
                    }}
                  />
                );
              })()}
              <input type="text" placeholder="Search checklist items…" value={filterSearch}
                onChange={e => setFilterSearch(e.target.value)} />
              <div className="cl-filter-actions">
                <button className="btn btn-sm btn-ghost" onClick={handleExport}>📥 Export</button>
                {!resetConfirm ? (
                  <button className="btn btn-sm btn-ghost" onClick={() => setResetConfirm(true)}>🔄 Reset</button>
                ) : (
                  <span className="cl-confirm-inline">
                    <span>Reset?</span>
                    <button className="btn btn-sm btn-danger" onClick={handleReset}>Yes</button>
                    <button className="btn btn-sm" onClick={() => setResetConfirm(false)}>No</button>
                  </span>
                )}
              </div>
            </div>

            {/* "Showing N of M" indicator + Reset filters affordance.
                Always visible so the user knows what their filters are doing. */}
            {viewMode === 'items' && (() => {
              const visible = filterCounts?.total_visible ?? totalItems;
              const total = filterCounts?.total_unfiltered ?? overview?.total_items ?? 0;
              const filtersActive = activeBuckets.size > 0
                || filterItemTypes.size > 0
                || !!filterSearch
                || scoreRange[0] > 0
                || scoreRange[1] < 100;
              return (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  fontSize: '0.85rem', color: '#6b7280',
                  padding: '4px 0', marginBottom: 4,
                }}>
                  <span>
                    {filtersActive
                      ? <>Showing <strong style={{color:'#111'}}>{visible}</strong> of {total} items</>
                      : <>{total} items</>}
                  </span>
                  {filtersActive && (
                    <button
                      className="btn btn-sm btn-ghost"
                      style={{ padding: '2px 8px', fontSize: '0.78rem' }}
                      onClick={() => {
                        setActiveBuckets(new Set());
                        setFilterItemTypes(new Set());
                        setFilterSearch('');
                        setScoreRange([0, 100]);
                      }}
                    >
                      Reset filters
                    </button>
                  )}
                </div>
              );
            })()}

            {/* Bulk-actions band — reserved space, slides in below filters
                when items are checked. Doesn't shift filter row above. */}
            {selected.size > 0 && (
            <div
              className="cl-bulk"
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px',
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: 6,
                marginBottom: 6,
              }}
            >
              <span className="cl-bulk-count" style={{ fontWeight: 600, color: '#1e40af' }}>
                {selected.size} item{selected.size === 1 ? '' : 's'} selected
              </span>
              {activeBuckets.has('suggested') && (
                <button className="btn btn-sm btn-primary" onClick={handleBulkConfirmSuggested}
                  disabled={bulkApplying}>
                  {bulkApplying ? '⏳' : `Confirm ${selected.size} suggested match${selected.size === 1 ? '' : 'es'}`}
                </button>
              )}
              <select value={bulkStatus} onChange={e => setBulkStatus(e.target.value)}>
                {STATUSES.filter(s => s.key !== 'not_reviewed').map(s => <option key={s.key} value={s.key}>{s.icon} {s.label}</option>)}
              </select>
              <button className="btn btn-sm btn-ghost" onClick={handleBulkUpdate}
                disabled={bulkApplying}>
                {bulkApplying ? '⏳' : `Apply to ${selected.size} selected`}
              </button>
              <button
                className="btn btn-sm"
                onClick={handleBulkDelete}
                disabled={bulkApplying}
                style={{
                  marginLeft: 12,
                  background: '#fee2e2',
                  color: '#991b1b',
                  border: '1px solid #fecaca',
                }}
                title="Permanently delete selected items (use to clean up titles/headers that aren't real checklist items)"
              >
                {bulkApplying ? '⏳' : `🗑 Delete ${selected.size}`}
              </button>
              <button
                className="btn btn-sm btn-ghost"
                style={{ marginLeft: 'auto' }}
                onClick={() => setSelected(new Set())}
              >
                Clear selection
              </button>
            </div>
            )}

            {viewMode === 'items' && (
              <>
                {/* Column headers */}
                <div className="cl-col-headers">
                  {(() => {
                    const visibleIds = displayItems.map(i => i.id);
                    const visibleSelectedCount = visibleIds.filter(id => selected.has(id)).length;
                    const allChecked = visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;
                    const someChecked = visibleSelectedCount > 0 && !allChecked;
                    return (
                      <input
                        type="checkbox"
                        title={allChecked ? `Deselect all ${visibleIds.length} visible` : `Select all ${visibleIds.length} visible`}
                        checked={allChecked}
                        ref={el => { if (el) el.indeterminate = someChecked; }}
                        onChange={toggleSelectAll}
                        disabled={visibleIds.length === 0}
                      />
                    );
                  })()}
                  <span className="cl-col-left">📋 Checklist Item</span>
                  <span className="cl-col-right">📄 Matched Document</span>
                  <span className="cl-col-headers-spacer" aria-hidden="true" />
                </div>

                {/* Item list */}
                {browserLoading ? (
                  <div className="cl-loading">Loading…</div>
                ) : displayItems.length === 0 ? (
                  <div className="cl-empty-items">
                    {activeBuckets.size === 0 && filterItemTypes.size === 0 && !filterSearch ? (
                      <>
                        No items in this checklist yet.{' '}
                        <HelpLink slug="editing-your-checklist" label="Editing your checklist">
                          Learn how to create one →
                        </HelpLink>
                      </>
                    ) : 'No items match the current filters.'}
                  </div>
                ) : (
                  <div className="cl-item-list">
                    {displayItems.map(item => (
                      <UnifiedItemRow
                        key={item.id}
                        item={item}
                        checked={selected.has(item.id)}
                        onToggle={() => toggleSelect(item.id)}
                        candidates={candidatesByItem[item.id] || []}
                        selectedPairs={selectedPairs}
                        onTogglePair={togglePair}
                        onAcceptOne={acceptOne}
                        onDismissPair={dismissPair}
                        onMarkNoMatch={markOneNoMatch}
                        onBundleDecision={handleBundleDecision}
                        onRequestFeedback={queueReviewAction}
                        isReverse={hasReverseLayout}
                        latestRunBucket={latestRunBucketByItem[item.id]}
                        onShowDetail={setViewerDocId}
                        onTypeOverride={handleTypeOverride}
                        onRequestRematchItem={() => runAgentAutoMatch({ itemIds: [item.id] })}
                        onFindInInventory={handleFindInInventory}
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {viewMode === 'orphans' && (
              <OrphanDocumentsView
                loading={orphanLoading}
                data={orphanDocs}
                onShowDetail={setViewerDocId}
                onRefresh={fetchOrphans}
              />
            )}

            {/* Pagination — server-paginated checklist items */}
            {totalPages > 1 && (
              <div className="cl-pagination">
                <button className="btn btn-sm" disabled={page <= 1}
                  onClick={() => fetchItems(page - 1)}>← Prev</button>
                <span className="cl-page-info">Page {page} of {totalPages}</span>
                <button className="btn btn-sm" disabled={page >= totalPages}
                  onClick={() => fetchItems(page + 1)}>Next →</button>
              </div>
            )}

          </div>
        </>
      )}

      {pendingReviewAction && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px',
          }}
          onClick={() => { setPendingReviewAction(null); setReviewFeedback(DEFAULT_REVIEW_FEEDBACK); }}
        >
          <div
            style={{ background: '#fff', borderRadius: '12px', padding: '16px', width: '100%', maxWidth: '560px', boxShadow: '0 10px 35px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}
          >
            <h4 style={{ marginTop: 0, marginBottom: '8px' }}>Add review feedback</h4>
            <p style={{ marginTop: 0, color: '#555', fontSize: '0.94rem' }}>
              Add optional feedback for this no-match or N/A decision so the review stays self-guided and learnable.
            </p>
            <div style={{ marginBottom: '10px' }} ref={reasonCodeHelpRef}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Reason code</label>
              <select
                value={reviewFeedback.reasonCode}
                onChange={e => setReviewFeedback(prev => ({ ...prev, reasonCode: e.target.value }))}
                style={{ width: '100%' }}
              >
                {REVIEW_REASON_OPTIONS.map(opt => (
                  <option key={opt.value || 'blank'} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Narrative note</label>
              <textarea
                value={reviewFeedback.reviewerNote}
                onChange={e => setReviewFeedback(prev => ({ ...prev, reviewerNote: e.target.value }))}
                rows={4}
                placeholder="Optional note explaining why this is or is not a match"
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn btn-sm btn-ghost" onClick={() => { setPendingReviewAction(null); setReviewFeedback(DEFAULT_REVIEW_FEEDBACK); }}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={confirmPendingReviewAction} disabled={accepting}>
                {accepting ? 'Saving…' : 'Save decision'}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewerDocId && (
        <DocumentViewer
          documentId={viewerDocId}
          onClose={() => setViewerDocId(null)}
          onOpenDocument={setViewerDocId}
        />
      )}
    </div>
  );
}

// ── Sub-components ──

function OrphanDocumentsView({ loading, data, onShowDetail, onRefresh }) {
  if (loading && !data) return <div className="cl-loading">Loading unmatched documents…</div>;
  if (data?.error) {
    return (
      <div className="cl-empty-items" style={{ color: '#991b1b' }}>
        Failed to load unmatched documents: {data.error}
        <button className="btn btn-sm btn-ghost" style={{ marginLeft: '8px' }} onClick={onRefresh}>Retry</button>
      </div>
    );
  }
  const docs = data?.documents || [];
  const total = data?.total ?? docs.length;
  if (total === 0) {
    return (
      <div className="cl-empty-items">
        ✅ Every document in this case is claimed by a confirmed checklist match.
      </div>
    );
  }
  return (
    <>
      <div className="cl-col-headers">
        <span className="cl-col-left">📋 Checklist Item</span>
        <span className="cl-col-right">📄 Matched Document ({total} unmatched)</span>
      </div>
      <div className="cl-item-list">
        {docs.map(d => (
          <OrphanDocumentRow key={d.doc_id} doc={d} onShowDetail={onShowDetail} />
        ))}
      </div>
    </>
  );
}

// Single orphan-doc row, rendered with the same .cl-item / .cl-item-columns
// layout as UnifiedItemRow so both views feel like one consistent surface.
// Left column carries a "no checklist item matched" placeholder; right
// column carries the doc with a "Not matched" badge.
function OrphanDocumentRow({ doc, onShowDetail }) {
  const filename = doc.filename || doc.doc_id;
  return (
    <div className="cl-item cl-not-reviewed">
      <div
        className="cl-item-main"
        onClick={() => onShowDetail && onShowDetail(doc.doc_id)}
        style={{ cursor: onShowDetail ? 'pointer' : 'default' }}
      >
        <span className="cl-item-status" title="Document is not matched to any checklist item">📄</span>

        <div className="cl-item-columns">
          <div className="cl-item-panel">
            <div className="cl-item-panel-label">Checklist item</div>
            <div
              className="cl-item-label"
              title="No checklist item matched this document"
              style={{ color: '#9ca3af', fontStyle: 'italic' }}
            >
              No checklist item matched
            </div>
          </div>

          <div className="cl-item-panel cl-item-panel-doc">
            <div className="cl-item-panel-label">Matched document</div>
            <div className="cl-item-doc" title={filename}>
              {onShowDetail
                ? <a
                    className="cl-doc-link"
                    href="#"
                    onClick={e => { e.preventDefault(); e.stopPropagation(); onShowDetail(doc.doc_id); }}
                  >
                    {filename}
                  </a>
                : <span className="cl-doc-name">{filename}</span>
              }
            </div>
            <div className="cl-item-row-meta">
              <span className="cl-candidate-badge" title="No checklist item is matched to this document">
                ⚠️ Not matched
              </span>
              {doc.file_type && (
                <span className="cl-candidate-badge" style={{ color: '#6b7280' }}>
                  {doc.file_type}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProgressSection({ overview, matchResult, filterCounts }) {
  const {
    total_items = 0, status_counts = {}, completion_pct = 0,
    approved_count = 0, suggested_count = 0, no_suggestion_count = 0,
    document_count = 0, unique_document_count = 0, unique_items = 0,
  } = overview;
  // When unique_document_count is 0 but document_count > 0, file_hash_sha256
  // hasn't been populated on the docs yet — the duplicate count is undefined,
  // not "all of them are duplicates." Hide the pill in that case rather than
  // displaying a misleading number.
  const dupKnown = unique_document_count > 0 || document_count === 0;
  const dupCount = dupKnown ? document_count - unique_document_count : 0;
  // Prefer /checklist/items/counts (live, filter-aligned) over /overview
  // (overview's suggested_count uses a looser definition that double-counts
  // confirmed-with-candidate items; filter and banner used to disagree).
  // Fall back to overview / matchResult only when filterCounts hasn't loaded.
  // "Suggested" reads from decision_state (matcher proposed a
  // candidate, undecided) — NOT band (top candidate hit Exact/Near
  // score tier). The earlier band wiring underreported by ~92% on
  // checklists with weak-tier candidates. Aligned 2026-06-01 with
  // the filter popover audit.
  const fallbackSuggestedCount = (matchResult?.auto_accepted || 0) + (matchResult?.needs_review || 0);
  const suggestedCount =
    filterCounts?.decision_state?.suggested
    ?? suggested_count
    ?? fallbackSuggestedCount
    ?? 0;
  const confirmedCount =
    filterCounts?.decision_state?.confirmed
    ?? approved_count
    ?? 0;
  const unresolvedCount =
    filterCounts?.decision_state?.no_suggestion
    ?? no_suggestion_count
    ?? Math.max((status_counts.not_reviewed || 0) - suggestedCount, 0);
  return (
    <div className="cl-section cl-progress">
      <div className="cl-progress-bar-wrap">
        <div className="cl-progress-bar">
          <div className="cl-progress-fill" style={{ width: `${completion_pct}%` }} />
        </div>
        <span className="cl-progress-text">
          {completion_pct.toFixed(1)}% of checklist items have been confirmed
        </span>
      </div>

      {/* Documents row */}
      <div className="cl-status-row cl-doc-row">
        <span className="cl-row-label">Documents</span>
        <div className="cl-status-pill cl-doc-total">
          <span className="cl-status-icon">📄</span>
          <span className="cl-status-value">{document_count}</span>
          <span className="cl-status-label">Total</span>
        </div>
        {dupKnown && (
          <div className="cl-status-pill cl-doc-unique">
            <span className="cl-status-icon">🔹</span>
            <span className="cl-status-value">{unique_document_count}</span>
            <span className="cl-status-label">Unique</span>
          </div>
        )}
        {dupKnown && dupCount > 0 && (
          <div className="cl-status-pill cl-doc-dup">
            <span className="cl-status-icon">📋</span>
            <span className="cl-status-value">{dupCount}</span>
            <span className="cl-status-label">Duplicates</span>
          </div>
        )}
      </div>

      {/* Checklist items row */}
      <div className="cl-status-row">
        <span className="cl-row-label">Checklist Items ({total_items}{unique_items !== total_items ? ` / ${unique_items} unique` : ''})</span>
        <div className="cl-status-pill cl-matched">
          <span className="cl-status-icon">🤖</span>
          <span className="cl-status-value">{suggestedCount}</span>
          <span className="cl-status-label">Suggested Matches</span>
        </div>
        <div className="cl-status-pill cl-matched">
          <span className="cl-status-icon">✅</span>
          <span className="cl-status-value">{confirmedCount}</span>
          <span className="cl-status-label">Confirmed Matches</span>
        </div>
        <div className="cl-status-pill cl-not-reviewed">
          <span className="cl-status-icon">👀</span>
          <span className="cl-status-value">{unresolvedCount}</span>
          <span className="cl-status-label">Needs Review</span>
        </div>
        {STATUSES.filter(s => !['not_reviewed', 'matched'].includes(s.key)).map(s => {
          const count = status_counts[s.key] || 0;
          // Hide the N/A pill when the user has never used it. Most
          // paralegal workflows decide "found / not found" and skip
          // N/A entirely; surfacing a zero-count pill adds chrome
          // without informing.
          if (s.key === 'n_a' && count === 0) return null;
          return (
            <div key={s.key} className={`cl-status-pill ${s.cls}`}>
              <span className="cl-status-icon">{s.icon}</span>
              <span className="cl-status-value">{count}</span>
              <span className="cl-status-label">{s.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Reflex match-health banner. Renders only when the latest match-run
// looks weak per the deterministic probe. Surfaces a plain-English
// summary + a single "Try to improve" CTA. Never auto-fires.
//
// Copy is paralegal-targeted: no "tier," "ceiling," "label noise,"
// "normalization," or dollar/second cost previews. Telemetry still
// captures the underlying numbers; the user just doesn't see them.
function MatchHealthBanner({ health, loading, onImprove, onDismiss, totalItems }) {
  if (!health) return null;
  const total = totalItems || health.total_items || 0;
  const weakCount = (health.partial_tier_count || 0) + (health.items_with_no_candidate || 0);
  const willImprove = !health.normalize_applied;

  const headline = (() => {
    if (health.reason === 'no_match_run') {
      return 'No matches have been found yet for this checklist.';
    }
    if (total <= 0) return 'Some matches look uncertain.';
    return `About ${weakCount} of your ${total} items don't have a confident match yet.`;
  })();

  // No jargon, no cost line. The detail line tells the lawyer what to
  // expect, not how the matcher works under the hood.
  const detail = willImprove
    ? 'We can try to sharpen the matches in a few seconds.'
    : "We've already tried the quick improvement on this list. The remaining items may not have a matching document in this case.";

  const buttonLabel = loading
    ? 'Working…'
    : willImprove
      ? 'Try to improve'
      : 'Already improved';

  return (
    <div
      className="cl-profile-block-banner"
      style={{ borderLeft: '3px solid #2563eb', background: '#eff6ff' }}
    >
      <span>
        <strong>{headline}</strong> {detail}
      </span>
      <button
        type="button"
        className="btn btn-sm cl-profile-block-cta"
        onClick={onImprove}
        disabled={loading || !willImprove}
      >
        {buttonLabel}
      </button>
      <button
        type="button"
        className="btn btn-sm btn-ghost"
        onClick={onDismiss}
        title="Hide this notice until the next match."
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  );
}

function UnifiedItemRow({ item, checked, onToggle, candidates, selectedPairs, onTogglePair, onAcceptOne, onDismissPair, onMarkNoMatch, onBundleDecision, onRequestFeedback, isReverse, latestRunBucket, onShowDetail, onTypeOverride, onRequestRematchItem, onFindInInventory }) {
  const [detailOpen, setDetailOpen] = useState(false);
  // When the user clicks "Edit selection" on a bundle that's already in
  // a terminal decision state, this flips back to the editable form so
  // they can re-select and re-approve. Resets when item.id changes.
  const [editingBundleAfterDecision, setEditingBundleAfterDecision] = useState(false);
  const [seededSelection, setSeededSelection] = useState(false);
  const statusDef = STATUSES.find(s => s.key === item.status) || STATUSES[0];
  const decisionState = getDecisionState(item, latestRunBucket);
  const decisionDef = DECISION_STATES[decisionState] || DECISION_STATES.no_suggestion;

  const collapsedCandidates = useMemo(() => {
    if (candidates.length <= 1) return candidates;
    const groups = {};
    const order = [];
    for (const c of candidates) {
      const key = c.doc_hash || c.doc_id;
      if (!groups[key]) { groups[key] = []; order.push(key); }
      groups[key].push(c);
    }
    return order.map(key => {
      const group = groups[key];
      if (group.length === 1) return group[0];
      return { ...group[0], _copyCount: group.length, _copies: group };
    });
  }, [candidates]);

  const hasCandidates = collapsedCandidates.length > 0;
  const topCandidate = collapsedCandidates[0] ?? null;
  // `top_match` is populated by the items endpoint from the latest
  // match_run's match_candidates row (rank=1) — survives page reload
  // and doesn't depend on the SSE matchResult stream having replayed,
  // so the suggested doc is visible the moment the items list lands.
  const persistedTop = item.top_match || null;
  // Prefer the persisted top_match (from match_candidates with the
  // tier ceiling applied) when an item hasn't been user-confirmed.
  // The legacy SSE / matchResult samples come from match_scores, which
  // doesn't have the cap and saturates NEAR-tier matches at 100%.
  // user_confirmed (item.file_path / source_document_id) still wins
  // because that's the user's recorded decision.
  const displayedDocName = item.file_path || persistedTop?.doc_filename || latestRunBucket?.sample?.doc_filename || topCandidate?.doc_filename || null;
  const displayedDocId = item.source_document_id || persistedTop?.doc_id || latestRunBucket?.sample?.doc_id || topCandidate?.doc_id || null;
  // Use || (not ??) for match_confidence so that 0 falls through to the
  // persisted match / sample / top candidate rather than showing
  // "0% confidence".
  const displayedScore = (item.match_confidence || null) ?? persistedTop?.score ?? latestRunBucket?.sample?.score ?? (isReverse ? topCandidate?.score : topCandidate?.confidence) ?? null;
  // Construct an informative breadcrumb. Prefer the strongest-signal
  // tier surfaced by the matcher pipeline (e.g. "filename_exact (NEAR,
  // prefix_stripped)") so users can see WHY the score is what it is.
  // Falls back to legacy method strings or the persisted band.
  const displayedMethod = (() => {
    if (item.match_method) return item.match_method;
    if (latestRunBucket?.sample?.method) return latestRunBucket.sample.method;
    if (persistedTop?.top_matcher) {
      const tier = persistedTop.top_tier ? persistedTop.top_tier.toUpperCase() : null;
      const key  = persistedTop.top_key_kind || null;
      const detail = [tier, key].filter(Boolean).join(', ');
      return detail
        ? `${persistedTop.top_matcher} (${detail})`
        : persistedTop.top_matcher;
    }
    if (persistedTop) return `band/${persistedTop.band}`;
    return null;
  })();
  const bundleData = item?.row_data?.match_bundle && typeof item.row_data.match_bundle === 'object'
    ? item.row_data.match_bundle
    : null;
  // Items whose LLM profile / shape is `narrative` or `category` are inherently
  // multi-doc requests ("All body-worn camera footage…", "Police reports") —
  // they should enter bundle review even when requirement_type wasn't set by
  // the (separate) requirement_classifier service. 'description' is a legacy
  // alias for 'narrative'.
  const itemTypeForBundle = String(
    item.item_type || item.llm_profile?.item_type || ''
  ).toLowerCase();
  const isMultiDocItemType = (
    itemTypeForBundle === 'narrative'
    || itemTypeForBundle === 'description'
    || itemTypeForBundle === 'category'
  );
  const bundleMode = bundleData?.bundle_mode
    || (item.requirement_type && item.requirement_type !== 'specific_file' ? 'bundle' : null)
    || (isMultiDocItemType ? 'bundle' : null);
  const hasBundleReview = bundleMode === 'bundle' || !!bundleData;
  const includedDocIds = bundleData?.included_doc_ids || bundleData?.proposed_included_doc_ids || [];
  const borderlineDocIds = bundleData?.borderline_doc_ids || [];
  const candidateLookup = useMemo(() => {
    const map = new Map();
    for (const cand of candidates || []) {
      if (cand?.doc_id) map.set(cand.doc_id, cand);
    }
    return map;
  }, [candidates]);
  const normalizeBundleDocs = useCallback((entries) => {
    return (entries || []).map((entry) => {
      if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
        const docId = entry.document_id || entry.doc_id || entry.id || null;
        const fallback = docId ? candidateLookup.get(docId) : null;
        return {
          document_id: docId,
          filename: entry.filename || entry.doc_filename || fallback?.doc_filename || fallback?.filename || docId || 'Unknown document',
          score: entry.semantic_score ?? entry.score ?? fallback?.confidence ?? fallback?.score ?? null,
        };
      }
      const fallback = candidateLookup.get(entry);
      return {
        document_id: entry,
        filename: fallback?.doc_filename || fallback?.filename || entry || 'Unknown document',
        score: fallback?.confidence ?? fallback?.score ?? null,
      };
    }).filter(doc => doc.document_id || doc.filename);
  }, [candidateLookup]);
  const includedBundleDocs = useMemo(
    () => normalizeBundleDocs(bundleData?.included_docs || bundleData?.proposed_included_docs || includedDocIds),
    [normalizeBundleDocs, bundleData, includedDocIds]
  );
  const borderlineBundleDocs = useMemo(
    () => normalizeBundleDocs(bundleData?.borderline_docs || borderlineDocIds),
    [normalizeBundleDocs, bundleData, borderlineDocIds]
  );
  const includedDocIdSet = useMemo(
    () => new Set(includedBundleDocs.map(d => d.document_id).filter(Boolean)),
    [includedBundleDocs]
  );
  // When this is a multi-doc item-type (narrative / category) but no
  // explicit bundleData was attached by the matcher, fall back to using
  // the regular candidate list as the bundle. Without this fallback the
  // bundle UI renders with zero checkboxes — the user sees "Grouped
  // review" but no docs to pick.
  const fallbackBundleDocs = useMemo(() => {
    if (bundleData) return [];
    if (!isMultiDocItemType) return [];
    return collapsedCandidates.map((cand) => ({
      document_id: cand.doc_id,
      filename: cand.doc_filename,
      score: isReverse ? cand.score : cand.confidence,
    }));
  }, [bundleData, isMultiDocItemType, collapsedCandidates, isReverse]);
  const allBundleDocs = useMemo(
    () => bundleData
      ? [...includedBundleDocs, ...borderlineBundleDocs]
      : fallbackBundleDocs,
    [bundleData, includedBundleDocs, borderlineBundleDocs, fallbackBundleDocs]
  );

  // Auto-select the system-suggested (included) docs on first expand
  useEffect(() => {
    if (detailOpen && !seededSelection && includedDocIdSet.size > 0) {
      for (const docId of includedDocIdSet) {
        const pk = pairKey(item.id, docId);
        if (!selectedPairs.has(pk)) onTogglePair(item.id, docId);
      }
      setSeededSelection(true);
    }
  }, [detailOpen, seededSelection, includedDocIdSet, item.id, selectedPairs, onTogglePair]);

  const judgeSummary = bundleData?.llm_judge?.summary || null;
  const judgeConfidence = bundleData?.llm_judge?.confidence;
  const missingSignals = bundleData?.llm_judge?.missing_signals || [];
  const includedCount = includedBundleDocs.length;
  const borderlineCount = borderlineBundleDocs.length;
  const hasStructuredBundleData = !!bundleData && (
    includedCount > 0
    || borderlineCount > 0
    || !!bundleData?.decision_action
    || !!judgeSummary
  );
  const singleDocReview = hasStructuredBundleData && includedCount === 1 && borderlineCount === 0;
  const reviewBadgeLabel = hasStructuredBundleData
    ? (singleDocReview ? 'Grouped review' : 'Evidence bundle')
    : 'Grouped review';
  const reviewModeLabel = !hasStructuredBundleData
    ? 'Grouped review available'
    : singleDocReview
      ? 'Single reviewed document'
      : 'Multi-document review bundle';
  const reviewSummaryText = !hasStructuredBundleData
    ? 'This item can be reviewed as a grouped category or narrative item.'
    : singleDocReview
      ? 'The review narrowed this item down to one best document.'
      : `${allBundleDocs.length} candidate document${allBundleDocs.length === 1 ? '' : 's'} • ${includedCount} suggested`;
  const decisionActionLabel = bundleData?.decision_action === 'approve'
    ? 'Approved'
    : bundleData?.decision_action === 'partial'
      ? 'Approved (subset)'
      : bundleData?.decision_action === 'reject'
        ? 'Rejected'
        : null;
  // Bundle terminal state: a decision_action has been recorded AND the
  // user is not currently re-editing. When terminal, the action row
  // shows a summary instead of the approve/reject form, and the
  // candidate-doc list renders as included/excluded badges instead of
  // checkboxes. "Edit selection" toggles editingBundleAfterDecision back.
  const persistedIncludedIds = bundleData?.included_doc_ids || [];
  const persistedExcludedIds = bundleData?.excluded_doc_ids || [];
  const persistedIncludedSet = useMemo(
    () => new Set(persistedIncludedIds),
    [persistedIncludedIds]
  );
  const confirmedBundleSize = persistedIncludedIds.length;
  const bundleIsTerminal = !!bundleData?.decision_action && !editingBundleAfterDecision;
  const selectedBundleDocIds = useMemo(
    () => Array.from(selectedPairs)
      .map(key => key.split('::'))
      .filter(([itemId, docId]) => itemId === item.id && !!docId)
      .map(([, docId]) => docId),
    [selectedPairs, item.id]
  );
  const hasBundleSelection = selectedBundleDocIds.length > 0;
  const bundleSelectionSummary = hasBundleSelection
    ? `${selectedBundleDocIds.length} selected to include • ${allBundleDocs.length - selectedBundleDocIds.length} will be excluded`
    : `${includedCount} suggested — check the docs you want to include, then approve`;
  const latestRunDecisionState = latestRunBucket
    ? (latestRunBucket.key === 'none' ? 'no_suggestion' : 'suggested')
    : null;
  const shouldShowLatestRunBadge = !!latestRunDecisionState && latestRunDecisionState !== decisionState;
  const detailAvailable = true; // pills always open a drawer; profile is always present or informative

  // PR2b — derived view-state for the headline pills + drawer summaries.
  const profile = item.llm_profile || null;
  // 'description' was an old alias for 'narrative' before the enum was finalized.
  const normalizeType = (t) => (t === 'description' ? 'narrative' : t);
  const storedType = normalizeType(item.item_type || profile?.item_type || 'unknown');
  // Infer narrative/category from signal fields when the LLM set item_type="unknown"
  // inconsistently (it sometimes populates narrative_subject or canonical_category
  // while still returning unknown as the type).
  const effectiveType = storedType !== 'unknown'
    ? storedType
    : profile?.narrative_subject
      ? 'narrative'
      : profile?.canonical_category
        ? 'category'
        : 'unknown';
  const rowKey = item.id ? `Row ${String(item.id).slice(0, 8)}` : 'Untitled';
  const display = (item.label && item.label.trim())
    || (item.description && item.description.trim())
    || (item.requirement_type && item.requirement_type.trim())
    || rowKey;
  const isFallback = !(item.label && item.label.trim());

  // Profile pill: warn on needs_review, ok if reviewer overrode.
  const profilePillVariant = profile?.reviewer_overrode
    ? 'cl-item-pill-ok'
    : profile?.needs_review
      ? 'cl-item-pill-warn'
      : profile
        ? ''
        : 'cl-item-pill-muted';
  const profilePillIcon = profile?.reviewer_overrode ? '✓'
    : profile?.needs_review ? '👀'
    : profile ? ''
    : '·';
  const profileSummary = profile
    ? `${effectiveType}${profile.needs_review ? ' • needs review' : profile.reviewer_overrode ? ' • overridden' : ''}`
    : 'not yet profiled';

  // Match pill: ok if confirmed, default if suggested, muted if no match.
  const matchPillVariant = item.status === 'matched' && item.manually_verified
    ? 'cl-item-pill-ok'
    : item.status === 'no_match'
      ? 'cl-item-pill-muted'
      : displayedDocName
        ? ''
        : 'cl-item-pill-muted';
  const matchPillIcon = item.status === 'matched' && item.manually_verified ? '✓'
    : item.status === 'no_match' ? '❌'
    : item.status === 'n_a' ? '➖'
    : displayedDocName ? '🤖'
    : '·';
  // Multi-doc bundles: show the bundle as a unit ("3 docs confirmed" /
  // "3 docs suggested") rather than truncating one filename, which hid
  // the fact that a bundle was approved across multiple docs. Single-
  // doc bundles and filename items keep the filename label.
  const isMultiDocBundle = hasBundleReview && (
    confirmedBundleSize > 1 || allBundleDocs.length > 1
  );
  const bundleVerb = bundleIsTerminal
    ? (bundleData?.decision_action === 'approve' || bundleData?.decision_action === 'partial' ? 'confirmed'
       : bundleData?.decision_action === 'reject' ? 'rejected'
       : 'decided')
    : 'suggested';
  const bundleVisibleCount = bundleIsTerminal
    ? confirmedBundleSize
    : (selectedBundleDocIds.length || includedCount || allBundleDocs.length);
  const matchPillText = isMultiDocBundle && bundleVisibleCount > 0
    ? `${bundleVisibleCount} docs ${bundleVerb}`
    : displayedDocName
      ? (displayedDocName.length > 28 ? displayedDocName.slice(0, 28) + '…' : displayedDocName)
      : item.status === 'no_match' ? 'no match'
        : item.status === 'n_a' ? 'N/A'
        : 'none';
  const matchSummary = isMultiDocBundle && bundleVisibleCount > 0
    ? `${bundleVisibleCount} doc${bundleVisibleCount === 1 ? '' : 's'} ${bundleVerb}`
    : displayedDocName
      ? `${matchPillText}${displayedScore != null ? ` (${confidenceLabel(displayedScore).toLowerCase()})` : ''}`
      : (item.status === 'no_match' ? 'no match' : item.status === 'n_a' ? 'N/A' : 'no candidate');

  // Right-cell title: matched-document filename promoted out of the pill,
  // mirroring the left cell's checklist-item filename. Multi-doc bundles
  // collapse to a count; absent matches show a muted placeholder.
  const rightTitleText = isMultiDocBundle && bundleVisibleCount > 0
    ? `${bundleVisibleCount} documents`
    : displayedDocName
      ? displayedDocName
      : item.status === 'no_match' ? 'No match'
        : item.status === 'n_a' ? 'N/A'
        : 'No candidate';
  const rightTitleIsPlaceholder = !displayedDocName && !(isMultiDocBundle && bundleVisibleCount > 0);

  // Right-cell pill: verdict + confidence label only — filename is now
  // the title above. Plain-English Strong/Possible/Weak instead of the
  // raw percentage; lawyers can't interpret "60%" but "Possible" is a
  // judgment they make every day.
  // Decision state wins over score band: a confirmed/manual_pick item
  // is "Confirmed match" regardless of the underlying composite score
  // the matcher persisted. Without this ordering, manual picks would
  // read "Strong match" (score-tier label) on the row.
  // A re-cut carried this hand-confirmed match onto a document that no longer
  // holds all the pages it was confirmed against (the server sets and clears it).
  const recutCheck = (item.status === 'matched' && item.manually_verified && item.row_data?.recut_check) || null;
  const rightPillText = isMultiDocBundle && bundleVisibleCount > 0
    ? `${bundleVisibleCount} docs ${bundleVerb}`
    : item.status === 'no_match' ? 'No Match'
    : item.status === 'n_a' ? 'N/A'
    : (item.status === 'matched' && item.manually_verified) ? 'Confirmed match'
    : displayedScore != null ? `${confidenceLabel(displayedScore)} match`
    : displayedDocName ? 'Match'
    : 'No candidate';

  return (
    <div className={`cl-item ${statusDef.cls} ${hasCandidates ? 'cl-item-has-candidates' : ''}`}>
      {/* Two-column row: checklist item | matched document, divider through drawer. */}
      <div className="cl-item-headline" onClick={() => detailAvailable ? setDetailOpen(!detailOpen) : null}>
        <input type="checkbox" checked={checked} onChange={onToggle} onClick={e => e.stopPropagation()} />

        <div className="cl-item-cell cl-item-cell-left">
          <div className="cl-item-cell-title" title={display}>
            <span className="cl-item-status" title={decisionDef.label}>{decisionDef.icon}</span>
            <span
              className="cl-item-cell-title-text"
              style={isFallback ? { color: '#6b7280', fontStyle: 'italic', fontWeight: 500 } : undefined}
            >
              {display}
            </span>
          </div>
          {/* Item-type pill removed for paralegal-facing UI — "narrative"
              vs "filename" is internal classification noise. The row
              itself remains click-to-expand; full profile + reviewer-
              override controls live in the expand drawer. */}
        </div>

        <div className="cl-item-cell cl-item-cell-right">
          <div className="cl-item-cell-title" title={rightTitleText}>
            <span
              className="cl-item-cell-title-text"
              style={rightTitleIsPlaceholder ? { color: '#9ca3af', fontStyle: 'italic', fontWeight: 500 } : undefined}
            >
              {rightTitleText}
            </span>
          </div>
          <button
            className={`cl-item-pill ${matchPillVariant} ${detailOpen ? 'cl-item-pill-active' : ''}`}
            onClick={(e) => { e.stopPropagation(); setDetailOpen(o => !o); }}
            title={`Match — ${matchSummary}`}
          >
            {matchPillIcon} {rightPillText}
          </button>
          {recutCheck && (
            <span
              className="cl-item-pill cl-item-pill-warn"
              title={`You confirmed this match when the document held pages ${recutCheck.pages_were?.join('–')} of its file. The file was cut again and the document now holds pages ${recutCheck.pages_now?.join('–')}. Open it to make sure it is still the right one — confirming or changing the match clears this mark.`}
            >
              ⚠ Check — the document was re-cut
            </span>
          )}
        </div>

        {detailAvailable && (
          <span className="cl-item-expand">{detailOpen ? '▾' : '▸'}</span>
        )}
      </div>

      {/* Side-by-side detail: Profile (left) | Match (right). Single
          expansion controlled by detailOpen — pills and caret all toggle it. */}
      {detailOpen && (
        <div className="cl-detail-container">
          {/* Left-chrome spacer keeps the grid aligned with the headline above. */}
          <div className="cl-detail-spacer" aria-hidden="true" />

          {/* PROFILE — left column, under "Checklist Item" */}
          <div className="cl-detail-cell cl-detail-cell-left">
            <div className="cl-detail-cell-header">Profile · {profileSummary}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span className="cl-detail-label">Item type:</span>
                  <ItemTypeBadge
                    type={effectiveType}
                    needsReview={!!profile?.needs_review && !profile?.reviewer_overrode}
                    reviewerOverrode={!!profile?.reviewer_overrode}
                    subtitle={null}
                    onOverride={onTypeOverride ? (newType) => onTypeOverride(item.id, newType) : null}
                  />
                </div>
                {profile?.canonical_category && (
                  <div className="cl-detail-row">
                    <span className="cl-detail-label">Canonical category:</span> {profile.canonical_category}
                  </div>
                )}
                {profile?.narrative_subject && (
                  <div className="cl-detail-row">
                    <span className="cl-detail-label">Narrative subject:</span> {profile.narrative_subject}
                  </div>
                )}
                {profile?.confidence != null && (
                  <div className="cl-detail-row">
                    <span className="cl-detail-label">Confidence:</span> {Math.round(profile.confidence * 100)}%
                    {profile.model ? ` • ${profile.model}` : ''}
                  </div>
                )}
                {profile?.reasoning && (
                  <div className="cl-detail-row">
                    <span className="cl-detail-label">Reasoning:</span> {profile.reasoning}
                  </div>
                )}
                {item.description && (
                  <div className="cl-detail-row">
                    <span className="cl-detail-label">Description:</span> {item.description}
                  </div>
                )}
                {profile?.profile_dirty && (
                  <div className="cl-detail-row" style={{ color: '#92400e' }}>
                    ⚠ Profile changed since last match.
                    {onRequestRematchItem && (
                      <button
                        className="btn btn-sm btn-ghost"
                        style={{ marginLeft: 8 }}
                        onClick={(e) => { e.stopPropagation(); onRequestRematchItem(); }}
                      >
                        Re-match this item
                      </button>
                    )}
                  </div>
                )}
                {!profile && (
                  <div className="cl-detail-row" style={{ color: '#6b7280', fontStyle: 'italic' }}>
                    No LLM profile yet — click "Profile" in the matching panel above to enrich.
                  </div>
                )}
          </div>

          {/* MATCH — right column, under "Matched Document" */}
          <div className="cl-detail-cell cl-detail-cell-right">
            <div className="cl-detail-cell-header">Match · {matchSummary}</div>
                {/* Top candidate / current match */}
                <div className="cl-detail-row">
                  <span className="cl-detail-label">Top match:</span>
                  {displayedDocName
                    ? (displayedDocId && onShowDetail
                      ? <a className="cl-doc-link" href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onShowDetail(displayedDocId); }}>{displayedDocName}</a>
                      : displayedDocName)
                    : 'No document linked yet'}
                  {displayedScore != null && (
                    <span style={{ marginLeft: 8 }}>{Math.round(displayedScore * 100)}%</span>
                  )}
                  {displayedMethod && (
                    <span style={{ marginLeft: 6, color: '#6b7280' }}>via {displayedMethod}</span>
                  )}
                </div>

                {/* Decision state + latest-run badge */}
                <div className="cl-detail-row">
                  <span className="cl-detail-label">Decision state:</span> {decisionDef.label}
                  {shouldShowLatestRunBadge && (
                    <span style={{ marginLeft: 8, color: '#6b7280' }}>(latest run: {latestRunBucket.label})</span>
                  )}
                </div>

                {/* Why explanations */}
                {item.match_reasoning && (
                  <div className="cl-detail-row">
                    <span className="cl-detail-label">Why it matched:</span> {item.match_reasoning}
                  </div>
                )}
                {latestRunBucket?.sample?.recommended_action_label && (
                  <div className="cl-detail-row">
                    <span className="cl-detail-label">Recommended action:</span> {latestRunBucket.sample.recommended_action_label}
                  </div>
                )}
                {latestRunBucket?.sample?.review_reason && (
                  <div className="cl-detail-row">
                    <span className="cl-detail-label">Why this remains unconfirmed:</span> {latestRunBucket.sample.review_reason}
                  </div>
                )}
                {latestRunBucket?.sample?.why_not_auto && (
                  <div className="cl-detail-row">
                    <span className="cl-detail-label">Why this is still only a suggestion:</span> {latestRunBucket.sample.why_not_auto}
                  </div>
                )}
                {latestRunBucket?.sample?.runner_up_doc_filename && (
                  <div className="cl-detail-row">
                    <span className="cl-detail-label">Next-best alternative:</span> {latestRunBucket.sample.runner_up_doc_filename}
                    {latestRunBucket.sample.runner_up_score != null ? ` (${Math.round(latestRunBucket.sample.runner_up_score * 100)}%)` : ''}
                  </div>
                )}

                {/* Bundle review block (preserved) */}
                {hasBundleReview && (
                  <details style={{ marginTop: 4 }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                      {reviewBadgeLabel}
                      <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 6 }}>
                        — {reviewSummaryText}
                      </span>
                    </summary>
                    <div style={{ marginTop: 6, paddingLeft: 12 }}>
                      <div className="cl-detail-row">
                        <span className="cl-detail-label">Review mode:</span> {reviewModeLabel}
                      </div>
                      {decisionActionLabel && (
                        <div className="cl-detail-row">
                          <span className="cl-detail-label">Outcome:</span> {decisionActionLabel}
                        </div>
                      )}
                      <div className="cl-detail-row">
                        <span className="cl-detail-label">Membership:</span> {bundleSelectionSummary}
                      </div>
                      {bundleData?.notes && (
                        <div className="cl-detail-row">
                          <span className="cl-detail-label">Reviewer note:</span> {bundleData.notes}
                        </div>
                      )}
                      {judgeSummary && (
                        <div className="cl-detail-row">
                          <span className="cl-detail-label">Judge summary:</span> {judgeSummary}
                          {judgeConfidence != null ? ` (${Math.round(judgeConfidence * 100)}%)` : ''}
                        </div>
                      )}
                      {missingSignals.length > 0 && (
                        <div className="cl-detail-row">
                          <span className="cl-detail-label">Missing signals:</span> {missingSignals.join(' • ')}
                        </div>
                      )}
                      {allBundleDocs.length > 0 && (
                        <div className="cl-candidates cl-bundle-docs" style={{ marginTop: 6 }}>
                          <div className="cl-candidates-label">
                            {singleDocReview ? 'Reviewed document'
                              : bundleIsTerminal ? 'Final document set'
                              : 'Candidate documents'}
                          </div>
                          {allBundleDocs.map((doc, i) => {
                            const docId = doc.document_id;
                            const pk = docId ? pairKey(item.id, docId) : null;
                            const isSuggested = docId && includedDocIdSet.has(docId);
                            // For terminal bundles, show included/excluded
                            // status based on the persisted decision rather
                            // than the editable checkbox. Click "Edit
                            // selection" to flip back to the form.
                            const isPersistedIncluded = docId && persistedIncludedSet.has(docId);
                            return (
                              <div key={`${doc.document_id || doc.filename || 'doc'}-${i}`} className={`cl-candidate-row ${!bundleIsTerminal && pk && selectedPairs.has(pk) ? 'cl-candidate-selected' : ''}`}>
                                {bundleIsTerminal ? (
                                  <span
                                    title={isPersistedIncluded ? 'Included in confirmed bundle' : 'Excluded from confirmed bundle'}
                                    style={{
                                      display: 'inline-block',
                                      width: 18,
                                      textAlign: 'center',
                                      color: isPersistedIncluded ? '#0d9488' : '#d1d5db',
                                      fontWeight: 700,
                                    }}
                                  >
                                    {isPersistedIncluded ? '✓' : '—'}
                                  </span>
                                ) : (
                                  docId && (
                                    <input
                                      type="checkbox"
                                      checked={pk ? selectedPairs.has(pk) : false}
                                      onChange={() => onTogglePair(item.id, docId)}
                                      title={isSuggested ? 'Suggested — uncheck to exclude' : 'Check to include this document'}
                                    />
                                  )
                                )}
                                {doc.document_id && onShowDetail ? (
                                  <a className="cl-doc-link cl-candidate-doc" href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onShowDetail(doc.document_id); }}>
                                    {doc.filename}
                                  </a>
                                ) : (
                                  <span className="cl-candidate-doc">{doc.filename}</span>
                                )}
                                {bundleIsTerminal && (
                                  <span
                                    style={{
                                      fontSize: '0.72rem',
                                      padding: '1px 6px',
                                      borderRadius: 999,
                                      background: isPersistedIncluded ? '#d1fae5' : '#f3f4f6',
                                      color: isPersistedIncluded ? '#065f46' : '#6b7280',
                                    }}
                                  >
                                    {isPersistedIncluded ? 'included' : 'excluded'}
                                  </span>
                                )}
                                {doc.score != null && (
                                  <span className="cl-candidate-score">{Math.round(doc.score * 100)}%</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </details>
                )}

                {/* Alternates (collapsed by default — non-bundle only) */}
                {!hasBundleReview && hasCandidates && collapsedCandidates.length > 1 && (
                  <details style={{ marginTop: 4 }}>
                    <summary style={{ cursor: 'pointer', color: '#6366f1' }}>
                      Alternates ({collapsedCandidates.length - 1} hidden)
                    </summary>
                    <div className="cl-candidates" style={{ marginTop: 6 }}>
                      {collapsedCandidates.slice(1).map((cand, i) => {
                        const itemId = isReverse ? cand.best_item_id : cand.item_id;
                        const docId = cand.doc_id;
                        const score = isReverse ? cand.score : cand.confidence;
                        const level = confidenceLevel(score);
                        const colors = CONFIDENCE_COLORS[level];
                        return (
                          <div key={i} className="cl-candidate-row" style={{ borderLeftColor: colors.border }}>
                            <a className="cl-doc-link cl-candidate-doc" href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onShowDetail(docId); }}>
                              {cand.doc_filename}
                            </a>
                            {cand._copyCount > 1 && (
                              <span className="cl-dup-badge" title={`${cand._copyCount} identical copies in inventory`}>
                                {cand._copyCount} copies
                              </span>
                            )}
                            <span className="cl-candidate-score" style={{ background: colors.bg, color: colors.text }}>
                              {Math.round(score * 100)}%
                            </span>
                            <button
                              className="btn btn-sm btn-ghost"
                              title="Use this document as the match instead of the top candidate"
                              onClick={(e) => { e.stopPropagation(); onAcceptOne(itemId, docId); }}
                            >
                              Use this match
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </details>
                )}

                {/* Action row */}
                <div className="cl-review-actions" style={{ marginTop: 8 }}>
                  {hasBundleReview ? (
                    bundleIsTerminal ? (
                      // Bundle already decided — show summary + edit affordance
                      // instead of asking the user to re-approve what they
                      // already approved. "Edit selection" puts the bundle
                      // back into editable mode with the prior selection
                      // pre-checked (the seedSelection effect already does
                      // this when bundleData?.included_doc_ids is present).
                      <>
                        <span style={{ color: '#0d9488', fontWeight: 600, marginRight: 8 }}>
                          {decisionActionLabel || 'Decided'}
                          {confirmedBundleSize > 0 && ` — ${confirmedBundleSize} doc${confirmedBundleSize === 1 ? '' : 's'}`}
                        </span>
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={(e) => { e.stopPropagation(); setEditingBundleAfterDecision(true); }}
                        >
                          Edit selection
                        </button>
                        <button className="btn btn-sm btn-ghost" onClick={(e) => { e.stopPropagation(); onRequestFeedback?.({ type: 'bundle', action: 'n_a', item, docId: displayedDocId, docName: displayedDocName }); }}>
                          Mark N/A
                        </button>
                      </>
                    ) : (
                    <>
                      <button
                        className="btn btn-sm btn-primary"
                        disabled={!singleDocReview && !hasBundleSelection && includedCount === 0}
                        onClick={(e) => { e.stopPropagation(); setEditingBundleAfterDecision(false); onBundleDecision(item, 'approve', displayedDocId, displayedDocName); }}
                      >
                        {singleDocReview
                          ? 'Approve reviewed document'
                          : hasBundleSelection
                            ? `Approve ${selectedBundleDocIds.length} selected doc${selectedBundleDocIds.length === 1 ? '' : 's'}`
                            : includedCount > 0
                              ? `Approve ${includedCount} suggested doc${includedCount === 1 ? '' : 's'}`
                              : 'Select docs to approve'}
                      </button>
                      <button className="btn btn-sm btn-ghost" onClick={(e) => { e.stopPropagation(); onRequestFeedback?.({ type: 'bundle', action: 'reject', item, docId: displayedDocId, docName: displayedDocName }); }}>
                        {singleDocReview ? 'Reject suggestion' : 'Reject all bundle docs'}
                      </button>
                      <button className="btn btn-sm btn-ghost" onClick={(e) => { e.stopPropagation(); onRequestFeedback?.({ type: 'bundle', action: 'n_a', item, docId: displayedDocId, docName: displayedDocName }); }}>
                        Mark N/A
                      </button>
                      {editingBundleAfterDecision && (
                        <button className="btn btn-sm btn-ghost" onClick={(e) => { e.stopPropagation(); setEditingBundleAfterDecision(false); }}>
                          Cancel edit
                        </button>
                      )}
                    </>
                    )
                  ) : (
                    <>
                      {displayedDocId && !(decisionState === 'confirmed' && displayedDocId === item.source_document_id) && (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={(e) => { e.stopPropagation(); onAcceptOne(item.id, displayedDocId); }}
                          title={decisionState === 'confirmed' ? 'Swap the confirmed match to this candidate' : 'Approve this candidate as the match'}
                        >
                          {decisionState === 'confirmed' ? 'Swap to this match' : 'Approve match'}
                        </button>
                      )}
                      <button className="btn btn-sm btn-ghost" onClick={(e) => { e.stopPropagation(); onRequestFeedback?.({ type: 'document', action: 'no_match', item, docId: displayedDocId, docName: displayedDocName }); }}>
                        Mark no match
                      </button>
                      <button className="btn btn-sm btn-ghost" onClick={(e) => { e.stopPropagation(); onRequestFeedback?.({ type: 'document', action: 'n_a', item, docId: displayedDocId, docName: displayedDocName }); }}>
                        Mark N/A
                      </button>
                      {onFindInInventory && (
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={(e) => { e.stopPropagation(); onFindInInventory(item); }}
                          title="Open the Document Inventory in match mode to pick a document for this item"
                        >
                          📎 Find in inventory →
                        </button>
                      )}
                    </>
                  )}
                </div>

                {/* Advanced raw row_data — last, behind details */}
                {item.row_data && Object.keys(item.row_data).length > 0 && (
                  <details className="cl-detail-row" style={{ marginTop: 6 }}>
                    <summary className="cl-detail-label" style={{ cursor: 'pointer' }}>Advanced technical details</summary>
                    <div style={{ marginTop: 8 }}>
                      <pre className="cl-detail-raw">{JSON.stringify(item.row_data, null, 2)}</pre>
                    </div>
                  </details>
                )}
          </div>

          <div className="cl-detail-spacer" aria-hidden="true" />
        </div>
      )}
    </div>
  );
}
